import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  Box, Stack, Paper, Typography, Button, IconButton, Chip, Tooltip,
  TextField, MenuItem, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableHead, TableRow, TableCell, TableBody,
  Grid, LinearProgress, InputAdornment, Snackbar,
  CircularProgress, FormControlLabel, Switch, alpha, TableContainer,
  ToggleButtonGroup, ToggleButton, Collapse, Select, useMediaQuery, useTheme,
} from '@mui/material';
import PlayArrowRoundedIcon  from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon       from '@mui/icons-material/StopRounded';
import PauseRoundedIcon      from '@mui/icons-material/PauseRounded';
import AddIcon               from '@mui/icons-material/Add';
import EditOutlinedIcon      from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon     from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon        from '@mui/icons-material/AccessTime';
import SearchIcon            from '@mui/icons-material/Search';
import FileDownloadIcon      from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon      from '@mui/icons-material/PictureAsPdf';
import FolderOpenIcon        from '@mui/icons-material/FolderOpen';
import AssignmentIcon        from '@mui/icons-material/Assignment';
import TimerIcon             from '@mui/icons-material/Timer';
import BoltIcon              from '@mui/icons-material/Bolt';
import CalendarTodayIcon     from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon        from '@mui/icons-material/ExpandMore';
import ExpandLessIcon        from '@mui/icons-material/ExpandLess';
import AttachMoneyIcon       from '@mui/icons-material/AttachMoney';
import MoneyOffIcon          from '@mui/icons-material/MoneyOff';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon       from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon      from '@mui/icons-material/ChevronRight';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon          from '@mui/icons-material/CheckBox';
import WarningAmberIcon      from '@mui/icons-material/WarningAmber';
import ScheduleIcon          from '@mui/icons-material/Schedule';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import apiService from '../../services/apiServices';
import globals from '../../services/globals';
import usePermissions from '../../hooks/usePermissions';
import PERMISSIONS from '../../config/permissions/permissionsConfig';
import {
  COLLECTION_ID_KEY, normalizeProject, upsertById, removeById,
  crudErrorMessage, createWriteRunner,
} from './timeTrackerCrud';

// ─── helpers ─────────────────────────────────────────────────────────────────
const LS_KEY = 'nexuma_timer';

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtDur(s) {
  if (!s && s !== 0) return '—';
  s = Math.round(Number(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sc = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`;
}

function fmtDurCompact(s) {
  if (!s && s !== 0) return '—';
  s = Math.round(Number(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function calcDur(start, end) {
  if (!start || !end) return 0;
  const parse = t => { const p = t.split(':').map(Number); return p[0]*3600+(p[1]||0)*60+(p[2]||0); };
  const d = parse(end) - parse(start);
  return d > 0 ? d : 0;
}

function nowTimeStr() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
}

function fmtDateLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return 'Sot';
  if (diff === 1) return 'Dje';
  return d.toLocaleDateString('sq-AL', { weekday:'long', day:'numeric', month:'short' });
}

function getWeekStart() {
  const d = new Date(); const day = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - (day===0?6:day-1));
  return mon.toISOString().slice(0,10);
}

function getMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
}

// ─── shared sx ───────────────────────────────────────────────────────────────
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 } };

const cardSx = (theme) => ({
  p: 2, borderRadius: 2, border: '1px solid',
  borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
  bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#fff',
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.02)',
});

// ─── Cascade selects: Project → Group → Activity → Task ─────────────────────
function CascadeSelects({ projects, groups, activities, tasks, projId, groupId, actId, taskId,
  onProj, onGroup, onAct, onTask, disabled, size = 'small', showTask = true }) {
  const projGroups = groups.filter(g => String(g.project_id) === String(projId));
  const groupActs  = activities.filter(a => {
    if (!projId) return false;
    if (groupId) return String(a.group_id) === String(groupId);
    return String(a.project_id) === String(projId);
  });
  const projTasks = tasks.filter(t => String(t.ProjectId||t.project_id) === String(projId));
  return (
    <>
      <Grid item xs={12} sm={showTask ? 3 : 4}>
        <TextField select fullWidth size={size} label="Projekti *" value={projId}
          onChange={e => { onProj(e.target.value); if(onGroup) onGroup(''); onAct(''); if (onTask) onTask(''); }}
          disabled={disabled} sx={inputSx}>
          <MenuItem value=""><em style={{color:'#aaa'}}>— pa projekt —</em></MenuItem>
          {projects.map(p => {
            const sc={Active:'#059669','On Hold':'#d97706',Completed:'#6366f1',Cancelled:'#ef4444'}[p.Status]||'#64748b';
            return (
              <MenuItem key={p.Id} value={String(p.Id)}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{width:'100%'}}>
                  <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:p.Color||'#6366f1',flexShrink:0}}/>
                  <Typography sx={{flex:1,fontSize:'0.84rem'}}>{p.Name}</Typography>
                  {p.Status && <Box component="span" sx={{fontSize:'0.6rem',fontWeight:700,px:0.8,py:0.2,borderRadius:1,bgcolor:alpha(sc,0.12),color:sc,flexShrink:0}}>{p.Status}</Box>}
                </Stack>
              </MenuItem>
            );
          })}
        </TextField>
      </Grid>
      <Tooltip title={!projId ? 'Zgjidh projektin fillimisht' : ''} arrow>
        <Grid item xs={12} sm={showTask ? 3 : 4}>
          <TextField select fullWidth size={size} label="Grupi" value={groupId||''}
            onChange={e => { if(onGroup) onGroup(e.target.value); onAct(''); if(onTask) onTask(''); }}
            disabled={disabled || !projId} sx={inputSx}>
            <MenuItem value=""><em style={{color:'#aaa'}}>— pa grup —</em></MenuItem>
            {projGroups.map(g => (
              <MenuItem key={g.id} value={String(g.id)}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:g.color||'#8b5cf6',flexShrink:0}}/>
                  <span>{g.name}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Tooltip>
      <Tooltip title={!projId ? 'Zgjidh projektin fillimisht' : ''} arrow>
        <Grid item xs={12} sm={showTask ? 3 : 4}>
          <TextField select fullWidth size={size} label="Aktiviteti" value={actId}
            onChange={e => { onAct(e.target.value); if (onTask) onTask(''); }}
            disabled={disabled || !projId} sx={inputSx}>
            <MenuItem value=""><em style={{color:'#aaa'}}>— pa aktivitet —</em></MenuItem>
            {groupActs.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.title}</MenuItem>)}
          </TextField>
        </Grid>
      </Tooltip>
      {showTask && (
        <Tooltip title={!projId ? 'Zgjidh projektin fillimisht' : ''} arrow>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size={size} label="Detyra" value={taskId}
              onChange={e => onTask(e.target.value)}
              disabled={disabled || !projId} sx={inputSx}>
              <MenuItem value=""><em style={{color:'#aaa'}}>— pa detyre —</em></MenuItem>
              {projTasks.map(t => <MenuItem key={t.Id} value={String(t.Id)}>{t.Title}</MenuItem>)}
            </TextField>
          </Grid>
        </Tooltip>
      )}
    </>
  );
}

// ─── EntryDialog ─────────────────────────────────────────────────────────────
function EntryDialog({ open, initial, projects = [], groups = [], tasks = [], activities = [], onClose, onSave, saving }) {
  const blank = { date: todayStr(), start_time:'', end_time:'', description:'', project_id:'', group_id:'', activity_id:'', task_id:'', billable:true, duration_seconds:0 };
  const [form, setForm] = React.useState(blank);
  React.useEffect(() => {
    setForm(initial
      ? { ...blank, ...initial, project_id: String(initial.project_id||''), group_id: String(initial.group_id||''), activity_id: String(initial.activity_id||''), task_id: String(initial.task_id||'') }
      : blank);
  }, [open]); // eslint-disable-line
  const f = (k, v) => setForm(p => ({...p, [k]: v}));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{fontWeight:700, pb:1}}>{initial?.id ? 'Ndrysho regjistrim' : 'Regjistrim manual'}</DialogTitle>
      <DialogContent sx={{pt:'12px !important'}}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth size="small" type="date" label="Data" value={form.date}
              onChange={e => f('date', e.target.value)} InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={6} sm={3.5}>
            <TextField fullWidth size="small" type="time" label="Fillimi" value={form.start_time} inputProps={{step:1}}
              onChange={e => { f('start_time', e.target.value); f('duration_seconds', calcDur(e.target.value, form.end_time)); }}
              InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={6} sm={3.5}>
            <TextField fullWidth size="small" type="time" label="Mbarimi" value={form.end_time} inputProps={{step:1}}
              onChange={e => { f('end_time', e.target.value); f('duration_seconds', calcDur(form.start_time, e.target.value)); }}
              InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          {form.start_time && form.end_time && (
            <Grid item xs={12}>
              <Box sx={{bgcolor:t=>t.palette.mode==='dark'?alpha('#6366f1',0.08):alpha('#6366f1',0.04), borderRadius:1.5, px:2, py:0.8, display:'inline-flex', alignItems:'center', gap:1}}>
                <TimerIcon sx={{fontSize:14, color:'primary.main'}}/>
                <Typography variant="body2" sx={{fontWeight:700, color:'primary.main', fontFamily:'monospace'}}>{fmtDur(form.duration_seconds)}</Typography>
                <Typography variant="caption" sx={{color:'text.secondary'}}>kohëzgjatja</Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Pershkrimi" value={form.description}
              onChange={e => f('description', e.target.value)} multiline minRows={2} sx={inputSx}/>
          </Grid>
          <CascadeSelects projects={projects} groups={groups} activities={activities} tasks={tasks}
            projId={form.project_id} groupId={form.group_id} actId={form.activity_id} taskId={form.task_id}
            onProj={v=>{f('project_id',v); f('group_id',''); f('activity_id','');}} onGroup={v=>{f('group_id',v); f('activity_id','');}}
            onAct={v=>f('activity_id',v)} onTask={v=>f('task_id',v)}/>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.billable} onChange={e=>f('billable',e.target.checked)} size="small"/>}
              label={<Typography variant="body2">I faturueshëm</Typography>}/>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{px:3, pb:2}}>
        <Button onClick={onClose} sx={{borderRadius:2}}>Anulo</Button>
        <Button variant="contained" onClick={()=>onSave(form)} disabled={saving||!form.date}
          sx={{borderRadius:2}} startIcon={saving?<CircularProgress size={12} color="inherit"/>:null}>
          Ruaj
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ActivityDialog ───────────────────────────────────────────────────────────
function ActivityDialog({ open, initial, projects = [], groups = [], onClose, onSave, saving }) {
  const blank = { title:'', description:'', project_id:'', group_id:'', status:'Active' };
  const [form, setForm] = React.useState(blank);
  React.useEffect(() => {
    setForm(initial ? {...blank,...initial, project_id:String(initial.project_id||''), group_id:String(initial.group_id||'')} : blank);
  }, [open]); // eslint-disable-line
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  // kur zgjidhet grupi → vendos automatikisht project_id nga grupi
  const handleGroup = (gId) => {
    f('group_id', gId);
    if (gId) {
      const grp = groups.find(g => String(g.id) === String(gId));
      if (grp) f('project_id', String(grp.project_id || ''));
    }
  };

  const selectedGroup = groups.find(g => String(g.id) === String(form.group_id));
  const derivedProject = selectedGroup ? projects.find(p => String(p.Id) === String(selectedGroup.project_id)) : null;

  // grupimet per dropdown: grujet e organizuara sipas projektit
  const groupsByProject = projects
    .map(p => ({ proj: p, grps: groups.filter(g => String(g.project_id) === String(p.Id)) }))
    .filter(x => x.grps.length > 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{fontWeight:700, pb:1}}>{initial?.id ? 'Ndrysho aktivitetin' : 'Aktivitet i ri'}</DialogTitle>
      <DialogContent sx={{pt:'12px !important'}}>
        <Grid container spacing={1.5}>
          {/* Grupi zgjidhet i pari — projekti del automatikisht */}
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Grupi" value={form.group_id}
              onChange={e=>handleGroup(e.target.value)} sx={inputSx}>
              <MenuItem value=""><em style={{color:'#aaa'}}>— pa grup —</em></MenuItem>
              {groupsByProject.map(({proj, grps}) => [
                <MenuItem key={`hdr-${proj.Id}`} disabled sx={{opacity:1,pointerEvents:'none',py:0.3}}>
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:proj.Color||'#6366f1'}}/>
                    <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:0.5}}>{proj.Name}</Typography>
                  </Stack>
                </MenuItem>,
                ...grps.map(g=>(
                  <MenuItem key={g.id} value={String(g.id)} sx={{pl:3}}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:g.color||'#8b5cf6',flexShrink:0}}/>
                      <span>{g.name}</span>
                    </Stack>
                  </MenuItem>
                ))
              ])}
            </TextField>
          </Grid>

          {/* Projekti shfaqet si chip read-only kur vjen nga grupi, ose si dropdown kur nuk ka grup */}
          {derivedProject ? (
            <Grid item xs={12}>
              <Box sx={{display:'flex',alignItems:'center',gap:1,px:1.5,py:0.8,border:'1px solid',borderColor:'divider',borderRadius:1.5,bgcolor:t=>t.palette.mode==='dark'?alpha(derivedProject.Color||'#6366f1',0.08):alpha(derivedProject.Color||'#6366f1',0.06)}}>
                <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:derivedProject.Color||'#6366f1',flexShrink:0}}/>
                <Typography variant="caption" sx={{fontWeight:600,color:'text.secondary',flex:1}}>Projekti: <b style={{color:derivedProject.Color||'inherit'}}>{derivedProject.Name}</b></Typography>
                <Typography variant="caption" sx={{color:'text.disabled',fontSize:'0.68rem'}}>nga grupi</Typography>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Projekti" value={form.project_id}
                onChange={e=>f('project_id',e.target.value)} sx={inputSx}>
                <MenuItem value=""><em style={{color:'#aaa'}}>— zgjidh —</em></MenuItem>
                {projects.map(p=><MenuItem key={p.Id} value={String(p.Id)}>{p.Name}</MenuItem>)}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Titulli *" value={form.title}
              onChange={e=>f('title',e.target.value)} sx={inputSx}/>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Pershkrimi" value={form.description}
              onChange={e=>f('description',e.target.value)} multiline minRows={2} sx={inputSx}/>
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Statusi" value={form.status}
              onChange={e=>f('status',e.target.value)} sx={inputSx}>
              {['Active','In Progress','Done','On Hold'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{px:3, pb:2}}>
        <Button onClick={onClose} sx={{borderRadius:2}}>Anulo</Button>
        <Button variant="contained" onClick={()=>onSave(form)} disabled={saving||!form.title.trim()||(!form.project_id&&!form.group_id)}
          sx={{borderRadius:2}} startIcon={saving?<CircularProgress size={12} color="inherit"/>:null}>
          Ruaj
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── GroupDialog ──────────────────────────────────────────────────────────────
function GroupDialog({ open, initial, projects = [], onClose, onSave, saving }) {
  const blank = { name:'', description:'', project_id:'', color:'#8b5cf6', status:'Active' };
  const [form, setForm] = React.useState(blank);
  const [pickProject, setPickProject] = React.useState(false);
  React.useEffect(() => {
    setForm(initial ? {...blank,...initial, project_id:String(initial.project_id||'')} : blank);
    setPickProject(false);
  }, [open]); // eslint-disable-line
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const derivedProject = projects.find(p => String(p.Id) === String(form.project_id));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{fontWeight:700, pb:1}}>{initial?.id ? 'Ndrysho grupin' : 'Grup i ri'}</DialogTitle>
      <DialogContent sx={{pt:'12px !important'}}>
        <Grid container spacing={1.5}>
          {/* Projekti: chip read-only kur eshte i pre-selektuar, dropdown kur jo */}
          {derivedProject && !pickProject ? (
            <Grid item xs={12}>
              <Box sx={{display:'flex',alignItems:'center',gap:1,px:1.5,py:0.8,border:'1px solid',borderColor:'divider',borderRadius:1.5,bgcolor:t=>t.palette.mode==='dark'?alpha(derivedProject.Color||'#6366f1',0.08):alpha(derivedProject.Color||'#6366f1',0.06)}}>
                <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:derivedProject.Color||'#6366f1',flexShrink:0}}/>
                <Typography variant="caption" sx={{fontWeight:600,color:'text.secondary',flex:1}}>Projekti: <b style={{color:derivedProject.Color||'inherit'}}>{derivedProject.Name}</b></Typography>
                <Button size="small" sx={{fontSize:'0.65rem',minWidth:0,px:0.8,py:0.2,opacity:0.6,'&:hover':{opacity:1}}} onClick={()=>setPickProject(true)}>ndrysho</Button>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Projekti *" value={form.project_id}
                onChange={e=>{f('project_id',e.target.value); setPickProject(false);}} sx={inputSx}>
                <MenuItem value=""><em style={{color:'#aaa'}}>— zgjidh —</em></MenuItem>
                {projects.map(p=><MenuItem key={p.Id} value={String(p.Id)}>{p.Name}</MenuItem>)}
              </TextField>
            </Grid>
          )}

          <Grid item xs={9}>
            <TextField fullWidth size="small" label="Emri i grupit *" value={form.name}
              onChange={e=>f('name',e.target.value)} sx={inputSx}/>
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth size="small" label="Ngjyra" type="color" value={form.color}
              onChange={e=>f('color',e.target.value)} InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Pershkrimi" value={form.description}
              onChange={e=>f('description',e.target.value)} multiline minRows={2} sx={inputSx}/>
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Statusi" value={form.status}
              onChange={e=>f('status',e.target.value)} sx={inputSx}>
              {['Active','On Hold','Done'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{px:3, pb:2}}>
        <Button onClick={onClose} sx={{borderRadius:2}}>Anulo</Button>
        <Button variant="contained" onClick={()=>onSave(form)} disabled={saving||!form.name.trim()||!form.project_id}
          sx={{borderRadius:2}} startIcon={saving?<CircularProgress size={12} color="inherit"/>:null}>
          Ruaj
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── TaskDialog ───────────────────────────────────────────────────────────────
function TaskDialog({ open, initial, projects = [], activities = [], onClose, onSave, saving }) {
  const blank = { Title:'', Description:'', ProjectId:'', ActivityId:'', Status:'Pending', Priority:'Medium', DueDate:'', EstimatedHours:'' };
  const [form, setForm] = React.useState(blank);
  React.useEffect(() => {
    setForm(initial
      ? {...blank,...initial, ProjectId:String(initial.ProjectId||initial.project_id||''), ActivityId:String(initial.ActivityId||initial.activity_id||'')}
      : blank);
  }, [open]); // eslint-disable-line
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const projActs = activities.filter(a=>String(a.project_id)===String(form.ProjectId));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{fontWeight:700, pb:1}}>{initial?.Id ? 'Ndrysho detyrën' : 'Detyrë e re'}</DialogTitle>
      <DialogContent sx={{pt:'12px !important'}}>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Titulli *" value={form.Title}
              onChange={e=>f('Title',e.target.value)} sx={inputSx}/>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Projekti *" value={form.ProjectId}
              onChange={e=>{f('ProjectId',e.target.value); f('ActivityId','');}} sx={inputSx}>
              <MenuItem value=""><em style={{color:'#aaa'}}>— zgjidh —</em></MenuItem>
              {projects.map(p=><MenuItem key={p.Id} value={String(p.Id)}>{p.Name}</MenuItem>)}
            </TextField>
          </Grid>
          <Tooltip title={!form.ProjectId?'Zgjidh projektin fillimisht':''} arrow>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Aktiviteti" value={form.ActivityId}
                onChange={e=>f('ActivityId',e.target.value)} disabled={!form.ProjectId} sx={inputSx}>
                <MenuItem value=""><em style={{color:'#aaa'}}>— pa aktivitet —</em></MenuItem>
                {projActs.map(a=><MenuItem key={a.id} value={String(a.id)}>{a.title}</MenuItem>)}
              </TextField>
            </Grid>
          </Tooltip>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Pershkrimi" value={form.Description}
              onChange={e=>f('Description',e.target.value)} multiline minRows={2} sx={inputSx}/>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Prioriteti" value={form.Priority}
              onChange={e=>f('Priority',e.target.value)} sx={inputSx}>
              {['Low','Medium','High','Urgent'].map(p=><MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Statusi" value={form.Status}
              onChange={e=>f('Status',e.target.value)} sx={inputSx}>
              {['Pending','In Progress','Review','Completed'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Afati" value={form.DueDate}
              onChange={e=>f('DueDate',e.target.value)} InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="Orë të vlerësuara" value={form.EstimatedHours}
              onChange={e=>f('EstimatedHours',e.target.value)} sx={inputSx}/>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{px:3, pb:2}}>
        <Button onClick={onClose} sx={{borderRadius:2}}>Anulo</Button>
        <Button variant="contained" onClick={()=>onSave(form)} disabled={saving||!form.Title.trim()||!form.ProjectId}
          sx={{borderRadius:2}} startIcon={saving?<CircularProgress size={12} color="inherit"/>:null}>
          Ruaj
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ProjectDialog ────────────────────────────────────────────────────────────
function ProjectDialog({ open, initial, onClose, onSave, saving }) {
  const blank = { Name:'', Description:'', StartDate:'', EndDate:'', Status:'Active', BudgetHours:'', Color:'#6366f1', _groupName:'' };
  const [form, setForm] = React.useState(blank);
  React.useEffect(() => { setForm(initial ? {...blank,...initial, _groupName:''} : blank); }, [open]); // eslint-disable-line
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const isNew = !initial?.Id;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{fontWeight:700, pb:1}}>{initial?.Id ? 'Ndrysho projektin' : 'Projekt i ri'}</DialogTitle>
      <DialogContent sx={{pt:'12px !important'}}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={9}>
            <TextField fullWidth size="small" label="Emri *" value={form.Name}
              onChange={e=>f('Name',e.target.value)} sx={inputSx}/>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Ngjyra" type="color" value={form.Color}
              onChange={e=>f('Color',e.target.value)} InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Pershkrimi" value={form.Description}
              onChange={e=>f('Description',e.target.value)} multiline minRows={2} sx={inputSx}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Fillimi" value={form.StartDate}
              onChange={e=>f('StartDate',e.target.value)} InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="date" label="Mbarimi" value={form.EndDate}
              onChange={e=>f('EndDate',e.target.value)} InputLabelProps={{shrink:true}} sx={inputSx}/>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Statusi" value={form.Status}
              onChange={e=>f('Status',e.target.value)} sx={inputSx}>
              {['Active','On Hold','Completed','Cancelled'].map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="Buxheti (orë)" value={form.BudgetHours}
              onChange={e=>f('BudgetHours',e.target.value)} sx={inputSx}/>
          </Grid>
          {isNew && (
            <Grid item xs={12}>
              <Box sx={{pt:0.5,pb:0.3,borderTop:'1px dashed',borderColor:'divider'}}>
                <Typography variant="caption" sx={{color:'text.secondary',fontWeight:600,textTransform:'uppercase',letterSpacing:0.5,fontSize:'0.67rem',display:'block',mb:0.8}}>Grup i parë (opsional)</Typography>
                <TextField fullWidth size="small" label="Emri i grupit" value={form._groupName}
                  onChange={e=>f('_groupName',e.target.value)}
                  placeholder="p.sh. Backend, Dizajni..."
                  InputProps={{startAdornment:(
                    <Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:form.Color||'#8b5cf6',mr:1,flexShrink:0}}/>
                  )}} sx={inputSx}/>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{px:3, pb:2}}>
        <Button onClick={onClose} sx={{borderRadius:2}}>Anulo</Button>
        <Button variant="contained" onClick={()=>onSave(form)} disabled={saving||!form.Name.trim()}
          sx={{borderRadius:2}} startIcon={saving?<CircularProgress size={12} color="inherit"/>:null}>
          Ruaj
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── DelDialog ────────────────────────────────────────────────────────────────
function DelDialog({ open, onClose, onConfirm, saving }) {
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{sx:{borderRadius:2}}}>
      <DialogTitle sx={{fontWeight:700}}>Konfirmo fshirjen</DialogTitle>
      <DialogContent><Typography>Jeni të sigurt që dëshironi ta fshini këtë regjistrim?</Typography></DialogContent>
      <DialogActions sx={{px:3, pb:2}}>
        <Button onClick={onClose} sx={{borderRadius:2}}>Anulo</Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={saving}
          sx={{borderRadius:2}} startIcon={saving?<CircularProgress size={12} color="inherit"/>:null}>
          Fshi
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, color }) {
  const safeColor = (!color || color.includes('.')) ? '#6366f1' : color;
  return (
    <Paper elevation={0} sx={t => ({...cardSx(t), position:'relative', overflow:'hidden'})}>
      <Box sx={{position:'absolute', top:-8, right:-8, width:60, height:60, borderRadius:'50%', bgcolor:alpha(safeColor,0.08)}}/>
      <Stack spacing={0.3}>
        <Typography variant="caption" sx={{color:'text.secondary', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.05em', fontSize:'0.65rem'}}>{label}</Typography>
        <Typography sx={{fontWeight:800, fontSize:'1.3rem', fontFamily:'monospace', color:safeColor, lineHeight:1.1}}>{value}</Typography>
        {sub && <Typography variant="caption" sx={{color:'text.disabled', fontSize:'0.68rem'}}>{sub}</Typography>}
      </Stack>
      <Box sx={{position:'absolute', bottom:8, right:12, color:alpha(safeColor,0.25)}}>{icon}</Box>
    </Paper>
  );
}

// ─── DateGroup row ────────────────────────────────────────────────────────────
function DateGroup({ dateStr, entries, projects, tasks, activities, onEdit, onDelete, isDeleting }) {
  const [open, setOpen] = React.useState(true);
  const total    = entries.reduce((s,e)=>s+(e.duration_seconds||0),0);
  const billable = entries.reduce((s,e)=>s+(e.billable?(e.duration_seconds||0):0),0);
  const isToday  = dateStr === todayStr();
  // time range of the group
  const times    = entries.map(e=>e.start_time).filter(Boolean).sort();
  const ends     = entries.map(e=>e.end_time).filter(Boolean).sort();
  const rangeStr = times.length && ends.length ? `${times[0].slice(0,5)} – ${ends[ends.length-1].slice(0,5)}` : null;

  return (
    <Box sx={{borderBottom:'1px solid', borderColor:t=>t.palette.mode==='dark'?'grey.900':'grey.100'}}>
      {/* ── Group header ── */}
      <Box
        onClick={()=>setOpen(o=>!o)}
        sx={{
          display:'flex', alignItems:'center', gap:1.5, px:2, py:1,
          cursor:'pointer', userSelect:'none',
          bgcolor: t => t.palette.mode==='dark' ? alpha('#fff',0.015) : '#f8fafc',
          '&:hover':{ bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.03):alpha('#000',0.02) },
        }}>
        <Box sx={{color:isToday?'primary.main':'text.secondary', display:'flex', alignItems:'center'}}>
          {open ? <ExpandLessIcon sx={{fontSize:16}}/> : <ExpandMoreIcon sx={{fontSize:16}}/>}
        </Box>
        <CalendarTodayIcon sx={{fontSize:13, color:isToday?'primary.main':'text.secondary', flexShrink:0}}/>
        <Typography variant="body2" sx={{fontWeight:700, color:isToday?'primary.main':'text.primary', flex:1}}>
          {fmtDateLabel(dateStr)}
          {isToday && <Chip label="sot" size="small" sx={{ml:1, height:16, fontSize:'0.6rem', bgcolor:t=>alpha(t.palette.primary.main,0.1), color:'primary.main', fontWeight:700}}/>}
        </Typography>
        {/* right side: billable chip + total */}
        {billable > 0 && (
          <Tooltip title="Orë të faturueshme" arrow>
            <Chip icon={<AttachMoneyIcon sx={{fontSize:'11px !important'}}/>} label={fmtDurCompact(billable)}
              size="small" sx={{height:19, fontSize:'0.66rem', bgcolor:alpha('#059669',0.1), color:'#059669', fontWeight:700}}/>
          </Tooltip>
        )}
        <Typography sx={{fontFamily:'monospace', fontWeight:800, fontSize:'0.9rem', color:isToday?'primary.main':'text.primary', minWidth:76, textAlign:'right'}}>
          {fmtDur(total)}
        </Typography>
      </Box>

      {/* ── Summary sub-row ── */}
      {open && (
        <Box sx={{
          display:'flex', alignItems:'center', gap:1, px:2, py:0.7,
          bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.008):'#fafbfc',
          borderTop:'1px dashed', borderColor:t=>t.palette.mode==='dark'?'grey.900':'grey.200',
        }}>
          <CalendarTodayIcon sx={{fontSize:13, color:'text.disabled', flexShrink:0}}/>
          <Typography variant="caption" sx={{color:'text.secondary', fontWeight:600}}>
            {entries.length} {entries.length===1?'regjistrim':'regjistrime'}
          </Typography>
          <Typography variant="caption" sx={{color:'text.disabled'}}>•</Typography>
          <Typography variant="caption" sx={{fontFamily:'monospace', color:'text.secondary', fontWeight:600}}>{fmtDur(total)}</Typography>
          <Box sx={{flex:1}}/>
          {rangeStr && <Typography variant="caption" sx={{fontFamily:'monospace', color:'text.disabled', fontSize:'0.7rem'}}>{rangeStr}</Typography>}
          {billable > 0 && <AttachMoneyIcon sx={{fontSize:14, color:'#059669'}}/>}
        </Box>
      )}

      {/* ── Individual entries ── */}
      <Collapse in={open}>
        {entries.map(e => {
          const proj = projects.find(p=>p.Id===e.project_id);
          const act  = activities.find(a=>a.id===e.activity_id);
          const task = tasks.find(t=>t.Id===e.task_id);
          return (
            <Box key={e.id} sx={{
              display:'flex', alignItems:'center', gap:1.5, px:2, py:1.1,
              borderTop:'1px solid', borderColor:t=>t.palette.mode==='dark'?'grey.900':'grey.100',
              '&:hover .entry-actions':{ opacity:1 },
              '&:hover':{ bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.015):'#fafeff' },
            }}>
              {/* project color bar */}
              <Box sx={{width:3, height:34, borderRadius:4, flexShrink:0, bgcolor:proj?.Color||alpha('#94a3b8',0.3)}}/>

              {/* description + chips */}
              <Box sx={{flex:'1 1 auto', minWidth:0}}>
                <Typography variant="body2" sx={{fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:e.description?'text.primary':'text.disabled', fontStyle:e.description?'normal':'italic', fontSize:'0.84rem'}}>
                  {e.description || 'Pa përshkrim'}
                </Typography>
                <Stack direction="row" spacing={0.6} alignItems="center" sx={{mt:0.4, flexWrap:'wrap', gap:0.4}}>
                  {proj && <>
                    <Chip label={proj.Name} size="small" sx={{height:17, fontSize:'0.62rem', fontWeight:700, bgcolor:alpha(proj.Color||'#6366f1',0.1), color:proj.Color||'primary.main'}}/>
                    {proj.Status && (() => { const sc={Active:'#059669','On Hold':'#d97706',Completed:'#6366f1',Cancelled:'#ef4444'}[proj.Status]||'#64748b'; return <Chip label={proj.Status} size="small" sx={{height:17,fontSize:'0.6rem',fontWeight:700,bgcolor:alpha(sc,0.1),color:sc}}/>; })()}
                  </>}
                  {act  && <Chip icon={<BoltIcon sx={{fontSize:'10px !important'}}/>} label={act.title} size="small" sx={{height:17, fontSize:'0.62rem', fontWeight:600, bgcolor:alpha('#8b5cf6',0.1), color:'#8b5cf6'}}/>}
                  {task && <><KeyboardArrowRightIcon sx={{fontSize:12,color:'text.disabled'}}/><Typography variant="caption" sx={{color:'text.secondary',fontSize:'0.65rem'}}>{task.Title}</Typography></>}
                </Stack>
              </Box>

              {/* time range */}
              {e.start_time && e.end_time && (
                <Typography variant="caption" sx={{color:'text.secondary', fontFamily:'monospace', fontSize:'0.76rem', whiteSpace:'nowrap', flexShrink:0}}>
                  {e.start_time.slice(0,5)} – {e.end_time.slice(0,5)}
                </Typography>
              )}

              {/* billable indicator */}
              <Tooltip title={e.billable?'I faturueshëm':'Jo i faturueshëm'} arrow>
                <Box sx={{flexShrink:0, width:20, display:'flex', justifyContent:'center'}}>
                  {e.billable
                    ? <AttachMoneyIcon sx={{fontSize:16, color:'#059669'}}/>
                    : <MoneyOffIcon   sx={{fontSize:16, color:t=>t.palette.mode==='dark'?'grey.700':'grey.300'}}/>
                  }
                </Box>
              </Tooltip>

              {/* duration */}
              <Typography sx={{fontFamily:'monospace', fontWeight:800, fontSize:'0.9rem', color:'text.primary', minWidth:76, textAlign:'right', flexShrink:0}}>
                {fmtDur(e.duration_seconds)}
              </Typography>

              {/* hover actions */}
              <Stack direction="row" spacing={0.2} className="entry-actions" sx={{opacity:0, transition:'opacity 0.15s', flexShrink:0}}>
                <IconButton size="small" onClick={()=>onEdit(e)}
                  sx={{borderRadius:1.5,'&:hover':{color:'primary.main',bgcolor:t=>alpha(t.palette.primary.main,0.08)}}}>
                  <EditOutlinedIcon sx={{fontSize:14}}/>
                </IconButton>
                <IconButton size="small" color="error" disabled={!!isDeleting?.(e.id)} onClick={()=>onDelete(e.id)}
                  sx={{borderRadius:1.5,'&:hover':{bgcolor:t=>alpha(t.palette.error.main,0.08)}}}>
                  <DeleteOutlineIcon sx={{fontSize:14}}/>
                </IconButton>
              </Stack>
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
}

// ═══ Main Component ═══════════════════════════════════════════════════════════
export default function TimeTracking({ defaultTab = 0 }) {
  const [entries,    setEntries]    = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [tasks,      setTasks]      = useState([]);
  const [activities, setActivities] = useState([]);
  const [groups,     setGroups]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [busyKeys,   setBusyKeys]   = useState([]);
  const [tab,        setTab]        = useState(defaultTab);

  // Any write in flight — drives every dialog's "Ruaj"/"Fshi" disabled state.
  const saving = busyKeys.length > 0;
  const isBusy = (key) => busyKeys.includes(key);

  // The backend gates project writes behind requireRole('admin','superadmin',
  // 'menaxher'); mirror that here so nobody clicks a button that can only 403.
  const { can } = usePermissions();
  const canCreateProject = can(PERMISSIONS.PROJECTS_CREATE);
  const canEditProject   = can(PERMISSIONS.PROJECTS_EDIT) || can(PERMISSIONS.PROJECTS_MANAGE);

  // sync tab when route changes (defaultTab prop changes) – useLayoutEffect avoids flash
  useLayoutEffect(() => { setTab(defaultTab); }, [defaultTab]);
  const [q,          setQ]          = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [snack,      setSnack]      = useState('');

  // timer
  const [running,   setRunning]   = useState(false);
  const [tProj,     setTProj]     = useState('');
  const [tGroup,    setTGroup]    = useState('');
  const [tAct,      setTAct]      = useState('');
  const [tTask,     setTTask]     = useState('');
  const [tDesc,     setTDesc]     = useState('');
  const [tBill,     setTBill]     = useState(true);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [tStartDsp, setTStartDsp] = useState('');
  const [elapsed,   setElapsed]   = useState(0);
  const [tSaving,         setTSaving]         = useState(false);
  const startedAt    = useRef(null);
  const startTimeRef = useRef(null);
  const tickRef      = useRef(null);

  // dialogs
  const [entryDlg, setEntryDlg] = useState({open:false, data:null});
  const [projDlg,  setProjDlg]  = useState({open:false, data:null});
  const [taskDlg,  setTaskDlg]  = useState({open:false, data:null});
  const [actDlg,   setActDlg]   = useState({open:false, data:null});
  const [groupDlg, setGroupDlg] = useState({open:false, data:null});
  const [delDlg,   setDelDlg]   = useState({open:false, type:null, id:null});

  // restore timer
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(LS_KEY)||'null');
      if (s?.startedAt) {
        startedAt.current    = s.startedAt;
        startTimeRef.current = s.startTimeStr;
        setTProj(s.proj||''); setTGroup(s.group||''); setTAct(s.act||''); setTTask(s.task||'');
        setTDesc(s.desc||''); setTBill(s.bill!==false);
        setTStartDsp(s.startTimeStr||'');
        setElapsed(Math.floor((Date.now()-s.startedAt)/1000));
        setRunning(true);
      }
    } catch(_){}
  },[]);

  useEffect(()=>{
    if (running) tickRef.current = setInterval(()=>setElapsed(Math.floor((Date.now()-startedAt.current)/1000)),500);
    else clearInterval(tickRef.current);
    return ()=>clearInterval(tickRef.current);
  },[running]);

  // load
  const loadAll = useCallback(async()=>{
    setLoading(true);
    try {
      const [e,p,t,a,g] = await Promise.all([
        apiService().get('timeEntries'), apiService().get('projects'),
        apiService().get('tasks'),       apiService().get('taskActivities'),
        apiService().get('activityGroups'),
      ]);
      setEntries(e?.data||[]);
      // normalizeProject: /api/projects, the generic store handler and the demo
      // localStorage mode each answer a different casing. Everything below keys
      // off p.Id / p.Name, so flatten the differences here once.
      setProjects((p?.data||[]).map(normalizeProject));
      setTasks(t?.data||[]);   setActivities(a?.data||[]);
      setGroups(g?.data||[]);
    } catch(err){ setSnack(crudErrorMessage(err)); }
    finally{ setLoading(false); }
  },[]);
  useEffect(()=>{ loadAll(); },[loadAll]);

  // One write at a time per key. The guard inside is synchronous — `saving`
  // above cannot stop a double click on its own, because setState is async and
  // the second click of a double click lands before the re-render.
  const runWrite = React.useMemo(
    () => createWriteRunner({ setBusy: setBusyKeys, onError: (err) => setSnack(crudErrorMessage(err)) }),
    []
  );

  // save LS
  const saveLS = (proj,group,act,task,desc,bill)=>{
    if (!startedAt.current) return;
    try { localStorage.setItem(LS_KEY, JSON.stringify({startedAt:startedAt.current, startTimeStr:startTimeRef.current, proj,group,act,task,desc,bill})); } catch(_){}
  };

  const startTimer = async ()=>{
    if (!tProj){ setSnack('Zgjidh projektin për të filluar!'); return; }
    const ts=Date.now(), str=nowTimeStr();
    startedAt.current=ts; startTimeRef.current=str;
    setTStartDsp(str); setElapsed(0); setRunning(true);
    try{ localStorage.setItem(LS_KEY,JSON.stringify({startedAt:ts,startTimeStr:str,proj:tProj,group:tGroup,act:tAct,task:tTask,desc:tDesc,bill:tBill})); }catch(_){}
  };

  const discardTimer = ()=>{
    setRunning(false); clearInterval(tickRef.current);
    setElapsed(0); setTProj(''); setTGroup(''); setTAct(''); setTTask('');
    setTDesc(''); setTBill(true); setTStartDsp(''); setActiveTaskId(null);
    startedAt.current=null; startTimeRef.current=null;
    try{ localStorage.removeItem(LS_KEY); }catch(_){}
  };

  const stopTimer = async()=>{
    setRunning(false); clearInterval(tickRef.current);
    const endStr=nowTimeStr();
    const secs=Math.floor((Date.now()-startedAt.current)/1000);
    try{ localStorage.removeItem(LS_KEY); }catch(_){}
    if(secs<5){ discardTimer(); return; }
    const user=globals().getLogedInUser();
    setTSaving(true);
    try {
      const res = await apiService().post('timeEntries',{
        date:todayStr(), start_time:startTimeRef.current, end_time:endStr, duration_seconds:secs,
        description:tDesc, project_id:tProj||null, group_id:tGroup||null, activity_id:tAct||null,
        task_id:tTask||null, billable:tBill, worker_id:user?.WorkerId||user?.worker_id||null,
      });
      // Same rule as the dialog writers: a 200 without a body still saved the
      // row, so resync rather than leave it invisible until a manual refresh.
      if(res?.data) setEntries(prev=>[res.data,...prev]); else await loadAll();
      setSnack('✓ Regjistrimi u ruajt!');
    } catch(e){ setSnack(crudErrorMessage(e)); }
    finally{ setTSaving(false); }
    setTProj(''); setTGroup(''); setTAct(''); setTTask(''); setTDesc(''); setTBill(true); setTStartDsp(''); setElapsed(0); setActiveTaskId(null);
    startedAt.current=null; startTimeRef.current=null;
  };

  // CRUD
  // Every writer funnels through runWrite so a double click can only ever send
  // one request, and every failure lands in the same snackbar.
  //
  // A 200 whose body carries no `data` still means the row IS in the database.
  // The old code did `if (r?.data) setX(...)` and otherwise dropped it on the
  // floor while still closing the dialog and showing "✓ U ruajt!" — the exact
  // "I clicked Shto and nothing appeared" report. Now it resyncs instead.
  const applyWrite = async (setter, idKey, rec) => {
    if (rec) setter(p => upsertById(p, idKey, rec));
    else await loadAll();
  };

  const saveEntry = (data)=> runWrite('timeEntries:save', async()=>{
    const u = globals().getLogedInUser();
    const wid = data.worker_id || u?.WorkerId || u?.worker_id || null;
    const payload = { ...data, worker_id: wid };
    if(payload.id){
      const r = await apiService().put('timeEntries',payload);
      await applyWrite(setEntries, 'id', r?.data || payload);
    } else {
      const r = await apiService().post('timeEntries',payload);
      // Newest entry first — the list is date-descending.
      if(r?.data) setEntries(p=>[r.data,...p]); else await loadAll();
    }
    setEntryDlg({open:false,data:null}); setSnack('✓ U ruajt!');
  });

  const saveProject = (data)=> runWrite('projects:save', async()=>{
    const {_groupName, ...projData} = data;
    if(data.Id){
      const r = await apiService().put('projects',projData);
      await applyWrite(setProjects, 'Id', normalizeProject(r?.data || projData));
    } else {
      const r = await apiService().post('projects',projData);
      const created = r?.data ? normalizeProject(r.data) : null;
      await applyWrite(setProjects, 'Id', created);
      // nëse shtoi emrin e grupit, krijo grupin automatikisht
      if(created && _groupName?.trim()){
        const gr = await apiService().post('activityGroups',{name:_groupName.trim(),project_id:created.Id,color:projData.Color||'#8b5cf6',status:'Active'});
        await applyWrite(setGroups, 'id', gr?.data);
      }
    }
    setProjDlg({open:false,data:null}); setSnack('✓ Projekti u ruajt!');
  });

  const saveTask = (data)=> runWrite('tasks:save', async()=>{
    const r = data.Id ? await apiService().put('tasks',data)
                      : await apiService().post('tasks',data);
    await applyWrite(setTasks, 'Id', r?.data || (data.Id ? data : null));
    setTaskDlg({open:false,data:null}); setSnack('✓ Detyra u ruajt!');
  });

  const saveActivity = (data)=> runWrite('taskActivities:save', async()=>{
    const r = data.id ? await apiService().put('taskActivities',{...data,Id:data.id})
                      : await apiService().post('taskActivities',data);
    await applyWrite(setActivities, 'id', r?.data || (data.id ? data : null));
    setActDlg({open:false,data:null}); setSnack('✓ Aktiviteti u ruajt!');
  });

  const saveGroup = (data)=> runWrite('activityGroups:save', async()=>{
    const r = data.id ? await apiService().put('activityGroups',{...data,Id:data.id})
                      : await apiService().post('activityGroups',data);
    await applyWrite(setGroups, 'id', r?.data || (data.id ? data : null));
    setGroupDlg({open:false,data:null}); setSnack('✓ Grupi u ruajt!');
  });
  const startFromTask = (t) => {
    if (running) { setSnack('Ndalo kohëmatësin aktual fillimisht!'); return; }
    const projId = t.ProjectId ? String(t.ProjectId) : (t.project_id ? String(t.project_id) : '');
    const actId  = t.ActivityId ? String(t.ActivityId) : (t.activity_id ? String(t.activity_id) : '');
    setTProj(projId);
    setTAct(actId);
    setTTask(String(t.Id));
    setTDesc(t.Title || '');
    setActiveTaskId(t.Id);
    // auto-start timer
    if (projId) {
      const ts = Date.now(), str = nowTimeStr();
      startedAt.current = ts; startTimeRef.current = str;
      setTStartDsp(str); setElapsed(0); setRunning(true);
      try { localStorage.setItem(LS_KEY, JSON.stringify({ startedAt: ts, startTimeStr: str, proj: projId, act: actId, task: String(t.Id), desc: t.Title || '', bill: true })); } catch(_) {}
    }
    setSnack(projId ? '▶ Kohëmatësi filloi për detyrën!' : '✓ Zgjidh projektin dhe fillo!');
  };
  // Setter per store, so delete/rollback is one code path instead of five ifs
  // (the old ifs also compared with !==, which missed a string id against the
  // numeric one the API returns).
  const COLLECTIONS = {
    timeEntries:    { list: entries,    set: setEntries    },
    projects:       { list: projects,   set: setProjects   },
    tasks:          { list: tasks,      set: setTasks      },
    taskActivities: { list: activities, set: setActivities },
    activityGroups: { list: groups,     set: setGroups     },
  };

  const handleDelete = ()=>{
    const {type,id} = delDlg;
    const col   = COLLECTIONS[type];
    const idKey = COLLECTION_ID_KEY[type];
    if(!col || !idKey || id===null || id===undefined || id===''){
      setDelDlg({open:false,type:null,id:null});
      setSnack('Gabim: rreshti nuk u identifikua — rifresko faqen.');
      return;
    }
    const before = col.list;   // rollback point, captured before the optimistic write
    setDelDlg({open:false,type:null,id:null});
    runWrite(`${type}:delete:${id}`, async()=>{
      col.set(removeById(before, idKey, id));   // optimistic — the row goes on click
      try {
        await apiService().delete(type,id);
        setSnack('✓ U fshi!');
      } catch(e){
        col.set(before);                        // put it back exactly as it was
        throw e;                                // runWrite reports it
      }
    });
  };

  // exports
  const exportXLSX = ()=>{
    const rows = filteredEntries.map(e=>[
      e.date, e.start_time||'', e.end_time||'', fmtDur(e.duration_seconds),
      projects.find(p=>p.Id===e.project_id)?.Name||'',
      activities.find(a=>a.id===e.activity_id)?.title||'',
      tasks.find(t=>t.Id===e.task_id)?.Title||'',
      e.description||'', e.billable?'Po':'Jo',
    ]);
    const ws=XLSX.utils.aoa_to_sheet([['Data','Fillimi','Mbarimi','Kohëzgjatja','Projekti','Aktiviteti','Detyra','Pershkrimi','Faturueshëm'],...rows]);
    const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,'Regjistrimet');
    XLSX.writeFile(wb,'time-tracking-'+todayStr()+'.xlsx');
  };
  const exportPDF = ()=>{
    const doc=new jsPDF({orientation:'landscape'});
    doc.setFontSize(14); doc.text('Regjistrimet e kohës',14,15);
    doc.autoTable({startY:22,
      head:[['Data','Fillimi','Mbarimi','Kohëzgjatja','Projekti','Aktiviteti','Faturueshëm']],
      body:filteredEntries.map(e=>[e.date,e.start_time||'',e.end_time||'',fmtDur(e.duration_seconds),
        projects.find(p=>p.Id===e.project_id)?.Name||'',
        activities.find(a=>a.id===e.activity_id)?.title||'',
        e.billable?'Po':'Jo']),
    });
    doc.save('time-tracking-'+todayStr()+'.pdf');
  };

  // derived
  const q_ = q.toLowerCase();
  const today_ = todayStr();
  const week_  = getWeekStart();
  const month_ = getMonthStart();

  const dateFiltered = entries.filter(e=>{
    if (dateFilter==='today') return e.date===today_;
    if (dateFilter==='week')  return e.date>=week_;
    if (dateFilter==='month') return e.date>=month_;
    return true;
  });
  const filteredEntries = dateFiltered.filter(e=>
    (e.description||'').toLowerCase().includes(q_) ||
    (projects.find(p=>p.Id===e.project_id)?.Name||'').toLowerCase().includes(q_)
  );

  // group entries by date descending
  const grouped = React.useMemo(()=>{
    const map={};
    filteredEntries.forEach(e=>{ if(!map[e.date]) map[e.date]=[]; map[e.date].push(e); });
    return Object.entries(map).sort((a,b)=>b[0].localeCompare(a[0]));
  },[filteredEntries]);

  const filteredProjects   = projects.filter(p=>(p.Name||'').toLowerCase().includes(q_));
  const filteredTasks      = tasks.filter(t=>(t.Title||'').toLowerCase().includes(q_));
  const filteredActivities = activities.filter(a=>(a.title||'').toLowerCase().includes(q_));
  const filteredGroups     = groups.filter(g=>(g.name||'').toLowerCase().includes(q_));

  const totalToday    = entries.filter(e=>e.date===today_).reduce((s,e)=>s+(e.duration_seconds||0),0);
  const totalWeek     = entries.filter(e=>e.date>=week_).reduce((s,e)=>s+(e.duration_seconds||0),0);
  const totalBillable = entries.filter(e=>e.date>=month_).reduce((s,e)=>s+(e.billable?e.duration_seconds||0:0),0);
  const totalAll      = entries.reduce((s,e)=>s+(e.duration_seconds||0),0);
  const billPct       = totalAll ? Math.round(totalBillable/totalAll*100) : 0;

  const tProjGroups = groups.filter(g=>String(g.project_id)===String(tProj));
  const tProjActs   = activities.filter(a=>{
    if (!tProj) return false;
    if (tGroup) return String(a.group_id)===String(tGroup);
    return String(a.project_id)===String(tProj);
  });
  const tProjTasks = tasks.filter(t=>String(t.ProjectId||t.project_id)===String(tProj));

  // ─── Responsive hooks ────────────────────────────────────────────────────────
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ─── Kanban / table view toggle ──────────────────────────────────────────────
  const [dayView, setDayView] = useState('kanban'); // 'kanban' | 'table'

  // ─── Mini calendar state ─────────────────────────────────────────────────────
  const [calMonth, setCalMonth] = useState(() => { const d=new Date(); return { y:d.getFullYear(), m:d.getMonth() }; });
  const [selDate, setSelDate] = useState(todayStr());

  // tasks for selected date (based on DueDate)
  const todayTasks  = tasks.filter(t => !t.DueDate || String(t.DueDate).slice(0,10) === selDate);
  const todayCount  = tasks.filter(t => !t.DueDate || String(t.DueDate).slice(0,10) === today_).length;

  // active task name (for timer header)
  const activeTask = running ? tasks.find(t=>String(t.Id)===String(activeTaskId)) : null;
  const activeTaskName = activeTask?.Title || tDesc || null;

  // ─── derived: estimated vs logged for active task ────────────────────────────
  const taskEstSec  = activeTask?.EstimatedHours ? Number(activeTask.EstimatedHours)*3600 : 0;
  const taskLogSec  = activeTask
    ? entries.filter(e=>String(e.task_id)===String(activeTask.Id)).reduce((s,e)=>s+(e.duration_seconds||0),0)
    : 0;
  const taskRemSec  = Math.max(0, taskEstSec - taskLogSec - (running ? elapsed : 0));
  const taskProgress = taskEstSec ? Math.min(100, Math.round((taskLogSec+(running?elapsed:0))/taskEstSec*100)) : 0;
  const isOverdue   = activeTask?.DueDate && String(activeTask.DueDate).slice(0,10) < today_;

  // ─── Kanban columns ──────────────────────────────────────────────────────────
  const KANBAN_COLS = [
    { key: 'todo',     label: 'PËR TU BËRË',   color: '#64748b', icon: <CheckBoxOutlineBlankIcon sx={{fontSize:15}}/> },
    { key: 'progress', label: 'NË PROCES',      color: '#f59e0b', icon: <PlayArrowRoundedIcon sx={{fontSize:15}}/> },
    { key: 'paused',   label: 'E PEZULLUAR',    color: '#8b5cf6', icon: <PauseRoundedIcon sx={{fontSize:15}}/> },
    { key: 'done',     label: 'E PËRFUNDUAR',   color: '#059669', icon: <CheckBoxIcon sx={{fontSize:15}}/> },
  ];
  const statusToCol = { Pending:'todo', 'In Progress':'progress', Review:'paused', Completed:'done' };
  const todayKanban = KANBAN_COLS.map(col => ({
    ...col,
    tasks: todayTasks.filter(t => (statusToCol[t.Status]||'todo') === col.key),
  }));

  // ─── Mini calendar helpers ────────────────────────────────────────────────────
  function buildCalDays(y, m) {
    const first = new Date(y, m, 1);
    const last  = new Date(y, m+1, 0);
    // start from Sunday
    const startDow = first.getDay();
    const days = [];
    for (let i=0; i<startDow; i++) days.push(null);
    for (let d=1; d<=last.getDate(); d++) days.push(d);
    return days;
  }
  const calDays = buildCalDays(calMonth.y, calMonth.m);
  const monthNames = ['Janar','Shkurt','Mars','Prill','Maj','Qershor','Korrik','Gusht','Shtator','Tetor','Nëntor','Dhjetor'];
  const DOW_LABELS = ['DIE','HËN','MAR','MËR','ENJ','PRE','SHT'];

  // tasks that have DueDate in a given day
  const tasksForDay = (d) => {
    if (!d) return [];
    const ds = `${calMonth.y}-${String(calMonth.m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return tasks.filter(t => String(t.DueDate||'').slice(0,10)===ds);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display:'flex', flexDirection:'column', minHeight:'100%', bgcolor: t=>t.palette.mode==='dark'?'#0b0f19':'#f8fafc' }}>
      {/* ══ MOBILE LAYOUT ════════════════════════════════════════════════════ */}
      {isMobile && (
        <Box sx={{ px:2, pt:2, pb:10, display:'flex', flexDirection:'column', gap:2 }}>

          {/* ── Mini Calendar ── */}
          <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:t.palette.mode==='dark'?'grey.800':'#eaeef2', bgcolor:t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden'})}>
            {/* header */}
            <Box sx={{px:2.5,pt:2.5,pb:1.5}}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb:0.5}}>
                <Typography sx={{fontWeight:900, fontSize:'1.5rem', letterSpacing:'-0.02em'}}>
                  {monthNames[calMonth.m]} {calMonth.y}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small"
                    onClick={()=>setCalMonth(p=>{const d=new Date(p.y,p.m-1,1);return{y:d.getFullYear(),m:d.getMonth()};})}
                    sx={{width:32,height:32,border:'1px solid',borderColor:'divider',borderRadius:1.5}}>
                    <ChevronLeftIcon sx={{fontSize:16}}/>
                  </IconButton>
                  <IconButton size="small"
                    onClick={()=>setCalMonth(p=>{const d=new Date(p.y,p.m+1,1);return{y:d.getFullYear(),m:d.getMonth()};})}
                    sx={{width:32,height:32,border:'1px solid',borderColor:'divider',borderRadius:1.5}}>
                    <ChevronRightIcon sx={{fontSize:16}}/>
                  </IconButton>
                </Stack>
              </Stack>
              <Typography variant="caption" sx={{color:'text.secondary',fontWeight:500}}>
                Mirësevini përsëri
              </Typography>
            </Box>

            {/* DOW labels */}
            <Grid container columns={7} sx={{px:1.5,pb:0.5}}>
              {DOW_LABELS.map(d=>(
                <Grid item xs={1} key={d} sx={{textAlign:'center'}}>
                  <Typography sx={{fontSize:'0.67rem',fontWeight:700,color:'text.secondary',letterSpacing:'0.04em'}}>{d}</Typography>
                </Grid>
              ))}
            </Grid>

            {/* Days */}
            <Grid container columns={7} sx={{px:1.5,pb:2}}>
              {calDays.map((d,i)=>{
                if(!d) return <Grid item xs={1} key={`e${i}`}/>;
                const ds=`${calMonth.y}-${String(calMonth.m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                const isToday=ds===today_;
                const isSel=ds===selDate;
                const hasTasks=tasksForDay(d).length>0;
                return (
                  <Grid item xs={1} key={d}>
                    <Box sx={{display:'flex',flexDirection:'column',alignItems:'center',py:0.4,cursor:'pointer'}} onClick={()=>setSelDate(ds)}>
                      <Box sx={{
                        width:36,height:36,borderRadius:'50%',
                        display:'flex',alignItems:'center',justifyContent:'center',
                        bgcolor:isToday?'#4f46e5':(isSel?alpha('#4f46e5',0.1):'transparent'),
                        color:isToday?'#fff':(isSel?'#4f46e5':'text.primary'),
                        fontWeight:isToday||isSel?800:400,
                        fontSize:'0.93rem',
                        transition:'all 0.15s',
                      }}>
                        {d}
                      </Box>
                      {hasTasks && (
                        <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:isToday?'#4f46e5':'#94a3b8',mt:0.3}}/>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* ── Tasks for selected day ── */}
          <Box>
            {todayTasks.length===0 ? (
              <Box sx={{textAlign:'center',py:6}}>
                <Box sx={{width:72,height:72,borderRadius:'50%',bgcolor:alpha('#6366f1',0.08),display:'flex',alignItems:'center',justifyContent:'center',mx:'auto',mb:2}}>
                  <AssignmentIcon sx={{fontSize:34,color:alpha('#6366f1',0.4)}}/>
                </Box>
                <Typography sx={{fontWeight:700,color:'text.primary',mb:0.5}}>Nuk ka detyra për sot</Typography>
                <Typography variant="body2" sx={{color:'text.secondary'}}>Shijoni ditën tuaj të lirë! 🎉</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {todayTasks.map(t=>{
                  const proj=projects.find(p=>p.Id===(t.ProjectId||t.project_id));
                  const sc={Pending:'#64748b','In Progress':'#f59e0b',Review:'#8b5cf6',Completed:'#059669'}[t.Status]||'#64748b';
                  const pc={Low:'#64748b',Medium:'#3b82f6',High:'#f97316',Urgent:'#ef4444'}[t.Priority]||'#64748b';
                  const isActive=running&&String(activeTaskId)===String(t.Id);
                  return (
                    <Paper key={t.Id} elevation={0} sx={tt=>({
                      borderRadius:2, border:'1px solid',
                      borderColor:isActive?alpha('#059669',0.4):alpha(proj?.Color||'#6366f1',0.25),
                      bgcolor:tt.palette.mode==='dark'?'#111827':'#fff',
                      p:1.5, overflow:'hidden', position:'relative',
                    })}>
                      <Box sx={{position:'absolute',left:0,top:0,bottom:0,width:4,bgcolor:proj?.Color||'#6366f1',borderRadius:'12px 0 0 12px'}}/>
                      <Box sx={{pl:1.5}}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                          <Box sx={{flex:1,minWidth:0}}>
                            <Typography sx={{fontWeight:700,fontSize:'0.9rem',mb:0.5,color:isActive?'#059669':'text.primary'}}>{t.Title}</Typography>
                            <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{gap:0.5}}>
                              {proj&&<Chip label={proj.Name} size="small" sx={{height:18,fontSize:'0.63rem',fontWeight:700,bgcolor:alpha(proj.Color||'#6366f1',0.1),color:proj.Color||'#6366f1'}}/>}
                              <Chip label={t.Status} size="small" sx={{height:18,fontSize:'0.63rem',fontWeight:700,bgcolor:alpha(sc,0.1),color:sc}}/>
                              <Chip label={t.Priority} size="small" sx={{height:18,fontSize:'0.63rem',fontWeight:700,bgcolor:alpha(pc,0.1),color:pc}}/>
                            </Stack>
                          </Box>
                          <IconButton size="small"
                            onClick={()=>isActive?stopTimer():startFromTask(t)}
                            sx={{width:36,height:36,border:'1px solid',borderColor:isActive?'#ef4444':'#059669',color:isActive?'#ef4444':'#059669',bgcolor:isActive?alpha('#ef4444',0.06):alpha('#059669',0.04),flexShrink:0}}>
                            {isActive?<StopRoundedIcon sx={{fontSize:18}}/>:<PlayArrowRoundedIcon sx={{fontSize:17}}/>}
                          </IconButton>
                        </Stack>
                        {t.DueDate&&<Typography variant="caption" sx={{color:'text.disabled',fontFamily:'monospace',fontSize:'0.7rem',display:'block',mt:0.5}}>{String(t.DueDate).slice(0,10)}</Typography>}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* ── Entries (compact) ── */}
          {entries.filter(e=>e.date===selDate).length>0&&(
            <Box>
              <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',mb:1}}>
                Regjistrimet
              </Typography>
              {entries.filter(e=>e.date===selDate).map(e=>{
                const proj=projects.find(p=>p.Id===e.project_id);
                return (
                  <Box key={e.id} sx={{display:'flex',alignItems:'center',gap:1.5,py:1,borderBottom:'1px solid',borderColor:'divider'}}>
                    <Box sx={{width:3,height:32,borderRadius:4,flexShrink:0,bgcolor:proj?.Color||alpha('#94a3b8',0.3)}}/>
                    <Box sx={{flex:1,minWidth:0}}>
                      <Typography variant="body2" sx={{fontWeight:500,fontSize:'0.83rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {e.description||'Pa përshkrim'}
                      </Typography>
                      {proj&&<Typography variant="caption" sx={{color:'text.secondary',fontSize:'0.7rem'}}>{proj.Name}</Typography>}
                    </Box>
                    <Typography sx={{fontFamily:'monospace',fontWeight:800,fontSize:'0.85rem',flexShrink:0}}>{fmtDur(e.duration_seconds)}</Typography>
                    <Stack direction="row" spacing={0.2} sx={{flexShrink:0}}>
                      <IconButton size="small" onClick={()=>setEntryDlg({open:true,data:e})} sx={{'&:hover':{color:'primary.main'}}}><EditOutlinedIcon sx={{fontSize:14}}/></IconButton>
                      <IconButton size="small" color="error" disabled={isBusy(`timeEntries:delete:${e.id}`)} onClick={()=>setDelDlg({open:true,type:'timeEntries',id:e.id})}><DeleteOutlineIcon sx={{fontSize:14}}/></IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ══ DESKTOP LAYOUT ════════════════════════════════════════════════════ */}
      {!isMobile && (
      <Box sx={{px:{xs:2,lg:4}, pt:2, pb:3, flex:1, display:'flex', flexDirection:'column', gap:2}}>

        {/* ── Page Header ── */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{
            width:44, height:44, borderRadius:2, flexShrink:0,
            background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <AccessTimeIcon sx={{color:'#fff', fontSize:22}}/>
          </Box>
          <Box sx={{flex:1, minWidth:0}}>
            <Typography variant="h6" sx={{fontWeight:800, letterSpacing:'-0.01em', lineHeight:1.15, fontSize:'1.1rem'}}>
              Time Tracking
            </Typography>
            <Typography variant="caption" sx={{color:'text.secondary', fontSize:'0.74rem'}}>
              Regjistro kohën, projektet dhe aktivitetet
            </Typography>
          </Box>
          {running && (
            <Chip
              icon={<Box sx={{width:7,height:7,borderRadius:'50%',bgcolor:'#059669',flexShrink:0,
                animation:'pulse-hdr 1s ease-in-out infinite',
                '@keyframes pulse-hdr':{'0%,100%':{opacity:1,transform:'scale(1)'},'50%':{opacity:0.5,transform:'scale(1.4)'}},
                ml:'4px !important',
              }}/>}
              label={fmtDur(elapsed)}
              sx={{fontFamily:'monospace',fontWeight:800,bgcolor:alpha('#059669',0.1),color:'#059669',fontSize:'0.88rem',border:'1px solid',borderColor:alpha('#059669',0.25)}}
            />
          )}
          <Button size="small" variant="contained" startIcon={<AddIcon sx={{fontSize:15}}/>}
            onClick={()=>setEntryDlg({open:true,data:null})}
            sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:34,px:2,flexShrink:0,boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.9)'}}}>
            Regjistrim i ri
          </Button>
        </Stack>

        {/* ── Stats Cards (3 cards matching screenshot) ── */}
        <Grid container spacing={2}>
          {/* Card 1: Kjo javë */}
          <Grid item xs={12} sm={4}>
            <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:t.palette.mode==='dark'?'grey.800':'#e8edf3', bgcolor:t.palette.mode==='dark'?'#111827':'#fff', p:2.5})}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{width:40,height:40,borderRadius:1.5,bgcolor:alpha('#6366f1',0.1),display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <AccessTimeIcon sx={{fontSize:20,color:'#6366f1'}}/>
                </Box>
                <Box sx={{flex:1,minWidth:0}}>
                  <Typography variant="caption" sx={{color:'text.secondary',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',fontSize:'0.62rem',display:'block'}}>KJO JAVË</Typography>
                  <Typography sx={{fontWeight:900,fontSize:'1.6rem',color:'text.primary',lineHeight:1.1,fontFamily:'monospace',letterSpacing:'-0.02em'}}>
                    {(() => { const h=Math.floor(totalWeek/3600); const m=Math.floor((totalWeek%3600)/60); return `${h}h ${m}m`; })()}
                  </Typography>
                  <Box sx={{mt:0.8}}>
                    <LinearProgress variant="determinate" value={Math.min(100, Math.round(totalWeek/(40*3600)*100))} sx={{height:4,borderRadius:2,bgcolor:alpha('#6366f1',0.12),'& .MuiLinearProgress-bar':{bgcolor:'#6366f1'}}}/>
                    <Typography variant="caption" sx={{color:'text.disabled',fontSize:'0.67rem'}}>{Math.min(100,Math.round(totalWeek/(40*3600)*100))}% e planit</Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          {/* Card 2: Sipas ligjit */}
          <Grid item xs={12} sm={4}>
            <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:t.palette.mode==='dark'?'grey.800':'#e8edf3', bgcolor:t.palette.mode==='dark'?'#111827':'#fff', p:2.5})}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{width:40,height:40,borderRadius:1.5,bgcolor:alpha('#8b5cf6',0.1),display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <ScheduleIcon sx={{fontSize:20,color:'#8b5cf6'}}/>
                </Box>
                <Box sx={{flex:1,minWidth:0}}>
                  <Typography variant="caption" sx={{color:'text.secondary',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',fontSize:'0.62rem',display:'block'}}>SIPAS LIGJIT</Typography>
                  <Typography sx={{fontWeight:900,fontSize:'1.6rem',color:'text.primary',lineHeight:1.1,letterSpacing:'-0.02em'}}>40 h/javë</Typography>
                  <Typography variant="caption" sx={{color:'text.disabled',fontSize:'0.67rem'}}>5 ditë × 8h ditore</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          {/* Card 3: Sot - detyra aktive */}
          <Grid item xs={12} sm={4}>
            <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:t.palette.mode==='dark'?'grey.800':'#e8edf3', bgcolor:t.palette.mode==='dark'?'#111827':'#fff', p:2.5})}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{width:40,height:40,borderRadius:1.5,bgcolor:alpha('#6366f1',0.1),display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <AssignmentIcon sx={{fontSize:20,color:'#6366f1'}}/>
                </Box>
                <Box sx={{flex:1,minWidth:0}}>
                  <Typography variant="caption" sx={{color:'text.secondary',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',fontSize:'0.62rem',display:'block'}}>SOT</Typography>
                  <Typography sx={{fontWeight:900,fontSize:'1.6rem',color:'text.primary',lineHeight:1.1,letterSpacing:'-0.02em'}}>
                    {todayCount} {todayCount===1?'detyrë aktive':'detyra aktive'}
                  </Typography>
                  <Typography variant="caption" sx={{color:'text.disabled',fontSize:'0.67rem'}}>{tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_&&t.Status!=='Completed').length} në plan sot</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* ── Active Timer Panel ── */}
        {running && (
          <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:alpha('#059669',0.3), bgcolor:t.palette.mode==='dark'?'#0d1f18':'#f0fdf7', overflow:'hidden'})}>
            {/* Header row */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{px:2.5,py:1.5,borderBottom:'1px solid',borderColor:alpha('#059669',0.15)}}>
              <Box sx={{width:9,height:9,borderRadius:'50%',bgcolor:'#059669',flexShrink:0,
                animation:'pulse-active 1s ease-in-out infinite',
                '@keyframes pulse-active':{'0%,100%':{opacity:1,transform:'scale(1)'},'50%':{opacity:0.5,transform:'scale(1.5)'}},
              }}/>
              <Typography sx={{fontWeight:700,fontSize:'0.82rem',color:'#059669'}}>Timer aktiv</Typography>
              {activeTaskName && (
                <Chip label={activeTaskName} size="small" sx={{fontSize:'0.75rem',fontWeight:600,height:22,bgcolor:alpha('#059669',0.1),color:'#059669',maxWidth:300}}/>
              )}
              <Box sx={{flex:1}}/>
              <Tooltip title="Hiq regjistrimin" arrow>
                <IconButton size="small" onClick={discardTimer} sx={{color:'text.disabled','&:hover':{color:'error.main'}}}>
                  <DeleteOutlineIcon sx={{fontSize:16}}/>
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Main timer area: 2 columns */}
            <Grid container>
              {/* Left: big clock */}
              <Grid item xs={12} md={7} sx={{p:3, borderRight:{md:'1px solid'}, borderColor:{md:alpha('#059669',0.12)}}}>
                {/* PAUZË badge placeholder */}
                <Box sx={{display:'inline-flex',alignItems:'center',gap:0.8,bgcolor:alpha('#475569',0.12),borderRadius:1,px:1.2,py:0.4,mb:2}}>
                  <PauseRoundedIcon sx={{fontSize:13,color:'text.secondary'}}/>
                  <Typography sx={{fontSize:'0.68rem',fontWeight:700,color:'text.secondary',letterSpacing:'0.08em'}}>PAUZË</Typography>
                </Box>

                {/* Big clock display */}
                <Typography sx={{
                  fontFamily:'monospace', fontWeight:900, fontSize:'3.5rem',
                  letterSpacing:'0.05em', color:'text.primary', lineHeight:1, mb:2.5,
                  textAlign:'center',
                }}>
                  {fmtDur(elapsed)}
                </Typography>

                {/* Hours + Min inputs */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{borderRadius:2, bgcolor:t=>t.palette.mode==='dark'?'#1e293b':'#f1f5f9', p:2, textAlign:'center'}}>
                      <Typography sx={{fontSize:'0.65rem',fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.08em',mb:0.5}}>ORË</Typography>
                      <Typography sx={{fontWeight:900,fontSize:'2rem',fontFamily:'monospace',lineHeight:1}}>
                        {String(Math.floor(elapsed/3600)).padStart(2,'0')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{borderRadius:2, bgcolor:t=>t.palette.mode==='dark'?'#1e293b':'#f1f5f9', p:2, textAlign:'center'}}>
                      <Typography sx={{fontSize:'0.65rem',fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.08em',mb:0.5}}>MIN</Typography>
                      <Typography sx={{fontWeight:900,fontSize:'2rem',fontFamily:'monospace',lineHeight:1}}>
                        {String(Math.floor((elapsed%3600)/60)).padStart(2,'0')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Stop button */}
                <Button fullWidth variant="contained" startIcon={<StopRoundedIcon/>}
                  onClick={stopTimer}
                  sx={{mt:2.5,borderRadius:1.5,fontWeight:700,textTransform:'none',height:42,bgcolor:'#ef4444','&:hover':{bgcolor:'#dc2626'},boxShadow:'none'}}>
                  Ndale Timer
                </Button>
              </Grid>

              {/* Right: progress panel */}
              <Grid item xs={12} md={5} sx={{p:3}}>
                {/* Progress bar */}
                <Stack direction="row" justifyContent="space-between" sx={{mb:0.5}}>
                  <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',fontSize:'0.7rem'}}>Progresi</Typography>
                  <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',fontSize:'0.7rem'}}>{taskProgress}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={taskProgress}
                  sx={{height:8,borderRadius:4,mb:2.5,bgcolor:t=>t.palette.mode==='dark'?'#1e293b':'#e2e8f0','& .MuiLinearProgress-bar':{bgcolor:taskProgress>=100?'#ef4444':'#059669',borderRadius:4}}}/>

                {/* 4-cell grid */}
                <Grid container spacing={1.5}>
                  {[
                    { label:'PLANIFIKUAR',    value: taskEstSec ? fmtDurCompact(taskEstSec) : '—',          color:'text.primary' },
                    { label:'E REGJISTRUAR',  value: fmtDurCompact(taskLogSec+elapsed)||'0m',                color:'#059669',      bg:alpha('#059669',0.08) },
                    { label:'MBETUR',         value: taskEstSec ? fmtDurCompact(taskRemSec)||'0m' : '—',    color:'text.primary' },
                    { label:'AKTUAL',         value: fmtDurCompact(elapsed)||'0m',                          color:'#059669',      bg:alpha('#059669',0.08) },
                  ].map(cell=>(
                    <Grid item xs={6} key={cell.label}>
                      <Box sx={{borderRadius:1.5,border:'1px solid',borderColor:'divider',p:1.3,bgcolor:cell.bg||'transparent'}}>
                        <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',mb:0.4}}>{cell.label}</Typography>
                        <Typography sx={{fontWeight:800,fontSize:'1rem',fontFamily:'monospace',color:cell.color,lineHeight:1}}>{cell.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Overdue warning */}
                {isOverdue && (
                  <Box sx={{mt:1.5,display:'flex',alignItems:'center',gap:1,bgcolor:alpha('#f59e0b',0.1),borderRadius:1.5,px:1.5,py:1,border:'1px solid',borderColor:alpha('#f59e0b',0.25)}}>
                    <WarningAmberIcon sx={{fontSize:16,color:'#f59e0b',flexShrink:0}}/>
                    <Typography variant="caption" sx={{color:'#b45309',fontWeight:700,fontSize:'0.73rem'}}>
                      Afati përfundoi. Mungojnë: {fmtDurCompact(taskRemSec)||'0m'}
                    </Typography>
                  </Box>
                )}
                {!isOverdue && taskEstSec>0 && taskRemSec===0 && (
                  <Box sx={{mt:1.5,display:'flex',alignItems:'center',gap:1,bgcolor:alpha('#f59e0b',0.1),borderRadius:1.5,px:1.5,py:1,border:'1px solid',borderColor:alpha('#f59e0b',0.25)}}>
                    <WarningAmberIcon sx={{fontSize:16,color:'#f59e0b',flexShrink:0}}/>
                    <Typography variant="caption" sx={{color:'#b45309',fontWeight:700,fontSize:'0.73rem'}}>
                      Afati përfundoi. Mungojnë: {fmtDurCompact(Math.max(0,taskLogSec+elapsed-taskEstSec))||'0m'}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* ── Quick Timer (when not running) ── */}
        {!running && (
        <Box sx={t=>({
          borderRadius:2, border:'1px solid', px:2, py:1.5,
          borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
          bgcolor: t.palette.mode==='dark'?'#111827':'#fff',
          transition:'all 0.25s',
        })}>
          <Stack direction={{xs:'column',sm:'row'}} spacing={1.5} alignItems={{xs:'stretch',sm:'center'}}>
            <TextField
              size="small"
              placeholder="Çfarë po punon? (opsionale)"
              value={tDesc}
              onChange={e=>setTDesc(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter'&&tProj) startTimer(); }}
              sx={{flex:1,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:36,fontSize:'0.84rem','& fieldset':{borderColor:'#e2e8f0'}}}}
            />
            <Select
              size="small"
              value={tProj}
              onChange={e=>{ setTProj(e.target.value); setTGroup(''); setTAct(''); setTTask(''); }}
              displayEmpty
              sx={{minWidth:160,'& .MuiSelect-select':{fontSize:'0.83rem',py:0.75},'& fieldset':{borderColor:'#e2e8f0'},'& .MuiOutlinedInput-root':{borderRadius:1.5,height:36}}}
            >
              <MenuItem value=""><em style={{color:'#94a3b8',fontStyle:'normal',fontSize:'0.83rem'}}>Projekti...</em></MenuItem>
              {projects.map(p=>(
                <MenuItem key={p.Id} value={String(p.Id)} sx={{fontSize:'0.83rem'}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:p.Color||'#6366f1',flexShrink:0}}/>
                    <span>{p.Name}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            <Tooltip title={tBill?'I faturueshëm':'Jo i faturueshëm'} arrow>
              <span>
                <IconButton size="small" onClick={()=>setTBill(b=>!b)}
                  sx={{width:36,height:36,flexShrink:0,border:'1px solid',
                    borderColor:tBill?alpha('#059669',0.4):'#e2e8f0',
                    color:tBill?'#059669':'text.disabled',
                    bgcolor:tBill?alpha('#059669',0.06):'transparent',
                    '&:hover':{borderColor:'#059669',color:'#059669'},
                  }}>
                  <AttachMoneyIcon sx={{fontSize:17}}/>
                </IconButton>
              </span>
            </Tooltip>
            <Button size="small" variant="contained"
              startIcon={<PlayArrowRoundedIcon sx={{fontSize:16}}/>}
              onClick={startTimer}
              disabled={!tProj}
              sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:36,px:2.5,flexShrink:0,boxShadow:'none',
                background:'linear-gradient(135deg,#059669,#047857)',
                '&:hover':{boxShadow:'none',filter:'brightness(0.92)'},
                '&.Mui-disabled':{opacity:0.4,background:'linear-gradient(135deg,#6b7280,#4b5563)'},
              }}>
              Start
            </Button>
          </Stack>
        </Box>
        )}

        {/* ── Planifikimi i ditës ── */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb:1.5}}>
            <Box>
              <Typography sx={{fontWeight:800,fontSize:'1rem',letterSpacing:'-0.01em'}}>Planifikimi i ditës</Typography>
              <Typography variant="caption" sx={{color:'text.secondary',fontSize:'0.73rem'}}>Orari & rekomandime</Typography>
            </Box>
          </Stack>

          {/* Fokusi i ditës card */}
          <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:t.palette.mode==='dark'?'grey.800':'#e8edf3', bgcolor:t.palette.mode==='dark'?'#111827':'#fff', mb:2, overflow:'hidden'})}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{px:2.5, py:1.5, borderBottom:'1px solid', borderColor:'divider'}}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{width:36,height:36,borderRadius:1.5,background:'linear-gradient(135deg,#6366f1,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <AssignmentIcon sx={{fontSize:18,color:'#fff'}}/>
                </Box>
                <Box>
                  <Typography sx={{fontWeight:800,fontSize:'0.9rem',lineHeight:1.2}}>Fokusi i ditës</Typography>
                  <Typography variant="caption" sx={{color:'text.secondary',fontSize:'0.7rem'}}>Orari i sugjeruar për sot</Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CalendarTodayIcon sx={{fontSize:13,color:'text.disabled'}}/>
                  <Typography variant="caption" sx={{color:'text.secondary',fontWeight:600,fontSize:'0.75rem'}}>
                    {new Date().toLocaleDateString('sq-AL',{weekday:'long',day:'numeric',month:'short'}).replace(/^\w/,c=>c.toUpperCase())}
                  </Typography>
                </Stack>
                <Chip
                  label={`${tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_&&t.Status!=='Completed').length} ${tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_&&t.Status!=='Completed').length===1?'detyrë':'detyra'}`}
                  size="small"
                  sx={{fontSize:'0.72rem',fontWeight:700,height:22,bgcolor:'#6366f1',color:'#fff'}}
                />
              </Stack>
            </Stack>
            <Box sx={{px:2.5,py:2}}>
              {tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_&&t.Status!=='Completed').length===0
                ? <Box sx={{py:3,textAlign:'center'}}>
                    <Typography sx={{color:'text.secondary',fontSize:'0.85rem'}}>Nuk ka detyra për sot.</Typography>
                    <Button size="small" variant="outlined" startIcon={<AddIcon/>} sx={{mt:1.5,borderRadius:1.5,textTransform:'none',fontWeight:700}}
                      onClick={()=>setTaskDlg({open:true,data:null})}>
                      Shto detyrë
                    </Button>
                  </Box>
                : <Stack spacing={1}>
                    {tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_&&t.Status!=='Completed').slice(0,3).map(t=>{
                      const proj=projects.find(p=>p.Id===(t.ProjectId||t.project_id));
                      const isActive=running&&String(activeTaskId)===String(t.Id);
                      return (
                        <Stack key={t.Id} direction="row" alignItems="center" spacing={1.5} sx={{
                          py:1,px:1.5,borderRadius:2,border:'1px solid',
                          borderColor:isActive?alpha('#059669',0.35):'divider',
                          bgcolor:isActive?t=>t.palette.mode==='dark'?alpha('#059669',0.08):alpha('#059669',0.03):'transparent',
                        }}>
                          <Box sx={{width:3,bottom:0,top:0,borderRadius:4,bgcolor:proj?.Color||'#6366f1',alignSelf:'stretch',flexShrink:0,minHeight:32}}/>
                          <Box sx={{flex:1,minWidth:0}}>
                            <Typography sx={{fontWeight:600,fontSize:'0.83rem',color:isActive?'#059669':'text.primary',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.Title}</Typography>
                            {proj&&<Typography variant="caption" sx={{color:'text.secondary',fontSize:'0.7rem'}}>{proj.Name}</Typography>}
                          </Box>
                          <Button size="small" variant="contained"
                            onClick={()=>isActive?stopTimer():startFromTask(t)}
                            sx={{borderRadius:1.5,height:32,px:1.5,textTransform:'none',fontWeight:700,boxShadow:'none',flexShrink:0,
                              bgcolor:isActive?'#ef4444':'#059669',
                              '&:hover':{bgcolor:isActive?'#dc2626':'#047857',boxShadow:'none'},
                              fontSize:'0.77rem',
                            }}
                            startIcon={isActive?<StopRoundedIcon sx={{fontSize:14}}/>:<PlayArrowRoundedIcon sx={{fontSize:14}}/>}>
                            {isActive?'Ndale':'Nis Timer'}
                          </Button>
                        </Stack>
                      );
                    })}
                  </Stack>
              }
            </Box>
          </Paper>

          {/* Pamje Tabelare / Kanban */}
          <Paper elevation={0} sx={t=>({borderRadius:2.5, border:'1px solid', borderColor:t.palette.mode==='dark'?'grey.800':'#e8edf3', bgcolor:t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden'})}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{px:2.5, py:1.5, borderBottom:'1px solid', borderColor:'divider'}}>
              <Box>
                <Typography sx={{fontWeight:800,fontSize:'0.9rem',lineHeight:1.2}}>Pamje Tabelare</Typography>
                <Typography variant="caption" sx={{color:'text.secondary',fontSize:'0.7rem'}}>Menaxhimi i detyrave</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {/* Kanban / Table toggle */}
                <Stack direction="row" sx={{border:'1px solid',borderColor:'divider',borderRadius:1.5,overflow:'hidden'}}>
                  <Tooltip title="Kanban" arrow>
                    <IconButton size="small"
                      onClick={()=>setDayView('kanban')}
                      sx={{borderRadius:0,width:32,height:32,color:dayView==='kanban'?'#6366f1':'text.disabled',bgcolor:dayView==='kanban'?alpha('#6366f1',0.08):'transparent'}}>
                      <ViewKanbanOutlinedIcon sx={{fontSize:17}}/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Tabelë" arrow>
                    <IconButton size="small"
                      onClick={()=>setDayView('table')}
                      sx={{borderRadius:0,width:32,height:32,color:dayView==='table'?'#6366f1':'text.disabled',bgcolor:dayView==='table'?alpha('#6366f1',0.08):'transparent',borderLeft:'1px solid',borderColor:'divider'}}>
                      <TableRowsOutlinedIcon sx={{fontSize:17}}/>
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Chip label={`${tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_).length} në plan`} size="small"
                  sx={{fontSize:'0.72rem',fontWeight:700,height:22,bgcolor:'#6366f1',color:'#fff'}}/>
              </Stack>
            </Stack>

            {/* Kanban view */}
            {dayView==='kanban' && (
              <Box sx={{p:2}}>
                <Grid container spacing={2}>
                  {todayKanban.map(col=>(
                    <Grid item xs={12} sm={6} md={3} key={col.key}>
                      <Box sx={{borderRadius:2,bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.03):'#f8fafc',border:'1px solid',borderColor:'divider',minHeight:200}}>
                        {/* column header */}
                        <Stack direction="row" alignItems="center" spacing={1} sx={{px:1.5,py:1,borderBottom:'1px solid',borderColor:'divider'}}>
                          <Box sx={{color:col.color,display:'flex',alignItems:'center'}}>{col.icon}</Box>
                          <Typography sx={{fontWeight:700,fontSize:'0.72rem',textTransform:'uppercase',letterSpacing:'0.07em',color:col.color,flex:1}}>{col.label}</Typography>
                          <Chip label={col.tasks.length} size="small" sx={{height:18,minWidth:24,fontSize:'0.65rem',fontWeight:700,bgcolor:alpha(col.color,0.1),color:col.color}}/>
                        </Stack>
                        {/* task cards */}
                        <Box sx={{p:1, display:'flex', flexDirection:'column', gap:0.75}}>
                          {col.tasks.length===0 ? (
                            <Box sx={{py:3,textAlign:'center'}}>
                              <Typography variant="caption" sx={{color:'text.disabled',fontSize:'0.72rem'}}>Asnjë detyrë</Typography>
                            </Box>
                          ) : col.tasks.map(t=>{
                            const proj=projects.find(p=>p.Id===(t.ProjectId||t.project_id));
                            const pc={Low:'#64748b',Medium:'#3b82f6',High:'#f97316',Urgent:'#ef4444'}[t.Priority]||'#64748b';
                            const due=String(t.DueDate||'').slice(0,10);
                            const over=due&&due<today_&&t.Status!=='Completed';
                            const isActive=running&&String(activeTaskId)===String(t.Id);
                            return (
                              <Paper key={t.Id} elevation={0} sx={tt=>({
                                p:1.2, borderRadius:1.5, border:'1px solid',
                                borderColor:isActive?alpha('#059669',0.4):alpha(proj?.Color||'#6366f1',0.2),
                                bgcolor:tt.palette.mode==='dark'?'#0f172a':'#fff',
                                position:'relative', overflow:'hidden',
                              })}>
                                {proj&&<Box sx={{position:'absolute',left:0,top:0,bottom:0,width:3,bgcolor:proj.Color||'#6366f1'}}/>}
                                <Box sx={{pl:proj?1:0}}>
                                  <Typography sx={{fontWeight:600,fontSize:'0.8rem',mb:0.6,lineHeight:1.3,color:isActive?'#059669':'text.primary'}}>{t.Title}</Typography>
                                  <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{gap:0.4,mb:0.8}}>
                                    <Chip label={t.Priority||'Medium'} size="small" sx={{height:16,fontSize:'0.58rem',fontWeight:700,bgcolor:alpha(pc,0.1),color:pc}}/>
                                    {over&&<Chip label="VONUAR" size="small" sx={{height:16,fontSize:'0.58rem',bgcolor:alpha('#ef4444',0.1),color:'error.main',fontWeight:700}}/>}
                                  </Stack>
                                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    {due&&<Typography variant="caption" sx={{color:over?'error.main':'text.disabled',fontFamily:'monospace',fontSize:'0.66rem'}}>{due}</Typography>}
                                    <IconButton size="small"
                                      onClick={()=>isActive?stopTimer():startFromTask(t)}
                                      sx={{width:26,height:26,border:'1px solid',borderColor:isActive?'#ef4444':'#059669',color:isActive?'#ef4444':'#059669',bgcolor:isActive?alpha('#ef4444',0.06):alpha('#059669',0.04),ml:'auto'}}>
                                      {isActive?<StopRoundedIcon sx={{fontSize:14}}/>:<PlayArrowRoundedIcon sx={{fontSize:13}}/>}
                                    </IconButton>
                                  </Stack>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Table view */}
            {dayView==='table' && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{'& th':{fontWeight:700,fontSize:'0.68rem',color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.06em',bgcolor:t=>t.palette.mode==='dark'?'#0d1117':'#f8fafc',py:1.2,borderBottom:'2px solid',borderColor:'divider'}}}>
                      {['Detyra','Projekti','Prioriteti','Statusi','Afati',''].map(h=><TableCell key={h}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_).length===0
                      ? <TableRow><TableCell colSpan={6}><Box sx={{py:4,textAlign:'center'}}><Typography sx={{color:'text.secondary',fontSize:'0.83rem'}}>Nuk ka detyra për sot.</Typography></Box></TableCell></TableRow>
                      : tasks.filter(t=>String(t.DueDate||'').slice(0,10)===today_).map(t=>{
                          const proj=projects.find(p=>p.Id===(t.ProjectId||t.project_id));
                          const sc={Pending:'#64748b','In Progress':'#f59e0b',Review:'#8b5cf6',Completed:'#059669'}[t.Status]||'#64748b';
                          const pc={Low:'#64748b',Medium:'#3b82f6',High:'#f97316',Urgent:'#ef4444'}[t.Priority]||'#64748b';
                          const due=String(t.DueDate||'').slice(0,10);
                          const over=due&&due<today_&&t.Status!=='Completed';
                          const isActive=running&&String(activeTaskId)===String(t.Id);
                          return (
                            <TableRow key={t.Id} sx={{'& td':{fontSize:'0.82rem',borderBottom:'1px solid',borderColor:'divider',py:1.2},'&:hover':{bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.02):alpha('#000',0.01)}}}>
                              <TableCell>
                                <Typography sx={{fontWeight:600,fontSize:'0.83rem',color:isActive?'#059669':'text.primary'}}>{t.Title}</Typography>
                              </TableCell>
                              <TableCell>{proj?<Chip label={proj.Name} size="small" sx={{bgcolor:alpha(proj.Color||'#6366f1',0.1),color:proj.Color||'#6366f1',fontWeight:700,fontSize:'0.68rem',height:19}}/>:'—'}</TableCell>
                              <TableCell><Chip label={t.Priority||'Medium'} size="small" sx={{bgcolor:alpha(pc,0.1),color:pc,fontWeight:700,fontSize:'0.68rem',height:19}}/></TableCell>
                              <TableCell><Chip label={t.Status} size="small" sx={{bgcolor:alpha(sc,0.1),color:sc,fontWeight:700,fontSize:'0.68rem',height:19}}/></TableCell>
                              <TableCell sx={{color:over?'error.main':'text.secondary',fontFamily:'monospace',fontSize:'0.75rem'}}>{due||'—'}</TableCell>
                              <TableCell align="right">
                                <Button size="small" variant="outlined"
                                  onClick={()=>isActive?stopTimer():startFromTask(t)}
                                  startIcon={isActive?<StopRoundedIcon sx={{fontSize:14}}/>:<PlayArrowRoundedIcon sx={{fontSize:14}}/>}
                                  sx={{borderRadius:1.5,textTransform:'none',fontWeight:700,height:30,px:1.5,borderColor:isActive?'#ef4444':'#059669',color:isActive?'#ef4444':'#059669','&:hover':{bgcolor:isActive?alpha('#ef4444',0.07):alpha('#059669',0.07)}}}>
                                  {isActive?'Stop':'Nis Timer'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>

        {/* Content card */}
        <Box sx={t=>({
          borderRadius:2, border:'1px solid',
          borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
          bgcolor: t.palette.mode==='dark'?'#111827':'#fff',
          flex:1, display:'flex', flexDirection:'column', overflow:'hidden',
          boxShadow: t.palette.mode==='dark'?'none':'0 1px 4px rgba(0,0,0,0.04)',
        })}>
          {loading && <LinearProgress sx={{height:2, bgcolor:'transparent', '& .MuiLinearProgress-bar':{ background:'linear-gradient(90deg,#6366f1,#8b5cf6)' }}}/>}

          {/* ── Toolbar ── */}
          <Stack direction={{xs:'column',sm:'row'}} alignItems="stretch" sx={{borderBottom:'1px solid', borderColor:'divider'}}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{px:2, py:1}}>
              <TextField size="small" placeholder="Kërko..." value={q} onChange={e=>setQ(e.target.value)}
                InputProps={{startAdornment:<InputAdornment position="start"><SearchIcon sx={{fontSize:15,color:'#94a3b8'}}/></InputAdornment>}}
                sx={{width:190, '& .MuiOutlinedInput-root':{borderRadius:1.5, height:34, fontSize:'0.84rem', '& fieldset':{borderColor:'#e2e8f0'}}}}/>

              {tab===0 && (
                <ToggleButtonGroup value={dateFilter} exclusive onChange={(_,v)=>v&&setDateFilter(v)} size="small"
                  sx={{
                    height:34,
                    '& .MuiToggleButton-root':{
                      px:1.5, fontSize:'0.74rem', fontWeight:600, textTransform:'none',
                      border:'1px solid #e2e8f0', color:'text.secondary',
                      '&.Mui-selected':{bgcolor:'#6366f1', color:'#fff', borderColor:'#6366f1', '&:hover':{bgcolor:'#4f46e5'}},
                      '&:first-of-type':{borderTopLeftRadius:6, borderBottomLeftRadius:6},
                      '&:last-of-type':{borderTopRightRadius:6, borderBottomRightRadius:6},
                    },
                  }}>
                  <ToggleButton value="today">Sot</ToggleButton>
                  <ToggleButton value="week">Javë</ToggleButton>
                  <ToggleButton value="month">Muaj</ToggleButton>
                  <ToggleButton value="all">Të gjitha</ToggleButton>
                </ToggleButtonGroup>
              )}

              {tab===0 && <>
                <Tooltip title="Eksporto XLSX" arrow>
                  <IconButton size="small" onClick={exportXLSX} sx={{border:'1px solid #e2e8f0',borderRadius:1.5,width:34,height:34,'&:hover':{borderColor:'#6366f1',color:'#6366f1'}}}>
                    <FileDownloadIcon sx={{fontSize:17}}/>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eksporto PDF" arrow>
                  <IconButton size="small" onClick={exportPDF} sx={{border:'1px solid #e2e8f0',borderRadius:1.5,width:34,height:34,'&:hover':{borderColor:'#6366f1',color:'#6366f1'}}}>
                    <PictureAsPdfIcon sx={{fontSize:17}}/>
                  </IconButton>
                </Tooltip>
              </>}
              {tab===0 && <Button size="small" variant="contained" startIcon={<AddIcon sx={{fontSize:16}}/>} onClick={()=>setEntryDlg({open:true,data:null})} sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:34,px:2,whiteSpace:'nowrap',boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.92)'}}}>+ Regjistrim</Button>}
              {tab===1 && canCreateProject && <Button size="small" variant="contained" startIcon={<AddIcon sx={{fontSize:16}}/>} onClick={()=>setProjDlg({open:true,data:null})} sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:34,px:2,whiteSpace:'nowrap',boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.92)'}}}>Shto Projekt</Button>}
              {tab===2 && <Button size="small" variant="contained" startIcon={<AddIcon sx={{fontSize:16}}/>} onClick={()=>setTaskDlg({open:true,data:null})} sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:34,px:2,whiteSpace:'nowrap',boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.92)'}}}>Shto Detyrë</Button>}
              {tab===3 && <Button size="small" variant="contained" startIcon={<AddIcon sx={{fontSize:16}}/>} onClick={()=>setActDlg({open:true,data:null})} sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:34,px:2,whiteSpace:'nowrap',boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.92)'}}}>Shto Aktivitet</Button>}
              {tab===4 && <Button size="small" variant="contained" startIcon={<AddIcon sx={{fontSize:16}}/>} onClick={()=>setGroupDlg({open:true,data:null})} sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',height:34,px:2,whiteSpace:'nowrap',boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.92)'}}}>Shto Grup</Button>}
            </Stack>
          </Stack>

          {/* ── ENTRIES (grouped by date) ── */}
          {tab===0 && (
            <Box>
              {grouped.length===0
                ? <Box sx={{py:10, textAlign:'center'}}>
                    <Box sx={{
                      width:72,height:72,borderRadius:'50%',mx:'auto',mb:2.5,
                      background:'linear-gradient(135deg,rgba(99,102,241,0.1),rgba(139,92,246,0.06))',
                      display:'flex',alignItems:'center',justifyContent:'center',
                    }}>
                      <AccessTimeIcon sx={{fontSize:36, color:'#6366f1'}}/>
                    </Box>
                    <Typography variant="body1" sx={{fontWeight:700, color:'text.primary', mb:0.75}}>Asnjë regjistrim</Typography>
                    <Typography variant="body2" sx={{color:'text.secondary', mb:3}}>
                      {dateFilter!=='all'?'Nuk ka regjistrime për periudhën e zgjedhur':'Fillo timerin ose shto një regjistrim manual'}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>setEntryDlg({open:true,data:null})}
                      sx={{borderRadius:1.5,fontWeight:700,textTransform:'none',px:3.5,boxShadow:'none',background:'linear-gradient(135deg,#6366f1,#8b5cf6)','&:hover':{boxShadow:'none',filter:'brightness(0.92)'}}}>
                      + Regjistrim i ri
                    </Button>
                  </Box>
                : grouped.map(([date, ents])=>(
                    <DateGroup key={date} dateStr={date} entries={ents}
                      projects={projects} tasks={tasks} activities={activities}
                      onEdit={e=>setEntryDlg({open:true,data:e})}
                      onDelete={id=>setDelDlg({open:true,type:'timeEntries',id})}
                      isDeleting={id=>isBusy(`timeEntries:delete:${id}`)}/>
                  ))
              }
            </Box>
          )}

          {/* ── PROJECTS ── */}
          {tab===1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{'& th':{fontWeight:700,fontSize:'0.68rem',color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.06em',bgcolor:t=>t.palette.mode==='dark'?'#0d1117':'#f8fafc',py:1.3,borderBottom:'2px solid',borderColor:t=>t.palette.mode==='dark'?'grey.800':'grey.200'}}}>
                    {['Projekti','Statusi','Orë buxhetore','Ore të regjistruara','Aktivitete','Detyra',''].map(h=><TableCell key={h}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProjects.length===0
                    ? <TableRow><TableCell colSpan={7}><Typography sx={{color:'text.secondary',py:3,textAlign:'center',fontSize:'0.82rem'}}>Nuk ka projekte.</Typography></TableCell></TableRow>
                    : filteredProjects.map(p=>{
                        const sc={Active:'#059669','On Hold':'#d97706',Completed:'#6366f1',Cancelled:'#ef4444'}[p.Status]||'#64748b';
                        const logS=entries.filter(e=>e.project_id===p.Id).reduce((s,e)=>s+(e.duration_seconds||0),0);
                        const bud=(p.BudgetHours||0)*3600;
                        const pct=bud?Math.min(100,Math.round(logS/bud*100)):0;
                        const aC=activities.filter(a=>String(a.project_id)===String(p.Id)).length;
                        const tC=tasks.filter(t=>(t.ProjectId||t.project_id)===p.Id).length;
                        return (
                          <TableRow key={p.Id} sx={{'&:hover':{bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.02):alpha('#000',0.01)},'& td':{fontSize:'0.82rem',borderBottom:'1px solid',borderColor:t=>t.palette.mode==='dark'?'grey.900':'grey.100',py:1.2}}}>
                            <TableCell>
                              <Stack direction="row" spacing={1.2} alignItems="center">
                                <Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:p.Color||'#6366f1',flexShrink:0}}/>
                                <Box>
                                  <Typography sx={{fontWeight:700,fontSize:'0.83rem'}}>{p.Name}</Typography>
                                  {p.Description&&<Typography variant="caption" sx={{color:'text.secondary',display:'block',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:200}}>{p.Description}</Typography>}
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell><Chip label={p.Status} size="small" sx={{bgcolor:alpha(sc,0.1),color:sc,fontWeight:600,fontSize:'0.68rem',height:19}}/></TableCell>
                            <TableCell sx={{color:'text.secondary'}}>{p.BudgetHours?p.BudgetHours+'h':'—'}</TableCell>
                            <TableCell>
                              {bud>0
                                ? <Box sx={{minWidth:120}}>
                                    <Stack direction="row" justifyContent="space-between" sx={{mb:0.4}}>
                                      <Typography variant="caption" sx={{color:'text.secondary',fontFamily:'monospace'}}>{fmtDurCompact(logS)}</Typography>
                                      <Typography variant="caption" sx={{color:pct>=90?'error.main':'text.secondary',fontWeight:pct>=90?700:400}}>{pct}%</Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={pct} sx={{height:4,borderRadius:2,bgcolor:t=>t.palette.mode==='dark'?'#1e293b':'#e2e8f0','& .MuiLinearProgress-bar':{bgcolor:pct>=90?'#ef4444':(p.Color||'#6366f1')}}}/>
                                  </Box>
                                : <Typography sx={{fontFamily:'monospace',color:logS>0?'text.primary':'text.disabled'}}>{logS>0?fmtDurCompact(logS):'—'}</Typography>}
                            </TableCell>
                            <TableCell>
                              {aC>0
                                ? <Chip icon={<BoltIcon sx={{fontSize:'11px !important'}}/>} label={aC} size="small"
                                    onClick={()=>{setTab(3);setQ(p.Name);}}
                                    sx={{bgcolor:alpha('#8b5cf6',0.1),color:'#8b5cf6',fontWeight:700,fontSize:'0.68rem',height:19,cursor:'pointer'}}/>
                                : <Typography variant="caption" sx={{color:'text.disabled'}}>0</Typography>}
                            </TableCell>
                            <TableCell>
                              {tC>0
                                ? <Chip icon={<AssignmentIcon sx={{fontSize:'11px !important'}}/>} label={tC} size="small"
                                    onClick={()=>{setTab(2);setQ(p.Name);}}
                                    sx={{bgcolor:alpha('#6366f1',0.1),color:'#6366f1',fontWeight:700,fontSize:'0.68rem',height:19,cursor:'pointer'}}/>
                                : <Typography variant="caption" sx={{color:'text.disabled'}}>0</Typography>}
                            </TableCell>
                            <TableCell align="right" sx={{pr:1}}>
                              <Stack direction="row" spacing={0.3} justifyContent="flex-end" className="entry-actions">
                                <Tooltip title="Shto aktivitet" arrow>
                                  <IconButton size="small" onClick={()=>setActDlg({open:true,data:{project_id:String(p.Id)}})} sx={{'&:hover':{color:'#8b5cf6',bgcolor:alpha('#8b5cf6',0.08)}}}>
                                    <BoltIcon sx={{fontSize:15}}/>
                                  </IconButton>
                                </Tooltip>
                                {canEditProject && <IconButton size="small" onClick={()=>setProjDlg({open:true,data:p})} sx={{'&:hover':{color:'primary.main',bgcolor:t=>alpha(t.palette.primary.main,0.08)}}}>
                                  <EditOutlinedIcon sx={{fontSize:15}}/>
                                </IconButton>}
                                {canEditProject && <IconButton size="small" color="error" disabled={isBusy(`projects:delete:${p.Id}`)} onClick={()=>setDelDlg({open:true,type:'projects',id:p.Id})} sx={{'&:hover':{bgcolor:t=>alpha(t.palette.error.main,0.08)}}}>
                                  <DeleteOutlineIcon sx={{fontSize:15}}/>
                                </IconButton>}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── TASKS ── */}
          {tab===2 && (
            <Box>
              {filteredTasks.length===0
                ? <Box sx={{py:8,textAlign:'center'}}>
                    <AssignmentIcon sx={{fontSize:48,color:'text.disabled',mb:1}}/>
                    <Typography sx={{color:'text.secondary'}}>Nuk ka detyra.</Typography>
                    <Typography variant="caption" sx={{color:'text.disabled',display:'block',mb:2}}>Shto detyra per projektet tuaja</Typography>
                    <Button variant="outlined" size="small" startIcon={<AddIcon/>} sx={{borderRadius:2}} onClick={()=>setTaskDlg({open:true,data:null})}>Detyrë e re</Button>
                  </Box>
                : filteredTasks.map(t=>{
                    const proj=projects.find(p=>p.Id===(t.ProjectId||t.project_id));
                    const act=activities.find(a=>a.id===(t.ActivityId||t.activity_id));
                    const due=t.DueDate?String(t.DueDate).slice(0,10):'';
                    const over=due&&due<today_&&t.Status!=='Completed';
                    const sc={Pending:'#64748b','In Progress':'#d97706',Review:'#6366f1',Completed:'#059669'}[t.Status]||'#64748b';
                    const pc={Low:'#64748b',Medium:'#3b82f6',High:'#f97316',Urgent:'#ef4444'}[t.Priority]||'#64748b';
                    const isActive = running && activeTaskId === t.Id;
                    const taskEntries = entries
                      .filter(e=>String(e.task_id)===String(t.Id))
                      .sort((a,b)=>b.date.localeCompare(a.date)||(b.start_time||'').localeCompare(a.start_time||''));
                    const totalTaskTime = taskEntries.reduce((s,e)=>s+(e.duration_seconds||0),0);
                    return (
                      <Box key={t.Id}>
                        {/* ── Task row ── */}
                        <Box sx={{
                          display:'flex', alignItems:'center', gap:1.5, px:2, py:1.3,
                          borderBottom:'1px solid',
                          borderColor:t=>isActive?(t.palette.mode==='dark'?alpha('#059669',0.35):alpha('#059669',0.3)):(t.palette.mode==='dark'?'grey.900':'grey.100'),
                          borderLeft: isActive ? '3px solid #059669' : '3px solid transparent',
                          bgcolor: isActive ? t=>t.palette.mode==='dark'?alpha('#059669',0.08):alpha('#059669',0.03) : 'transparent',
                          '&:hover':{bgcolor:t=>isActive?(t.palette.mode==='dark'?alpha('#059669',0.12):alpha('#059669',0.05)):(t.palette.mode==='dark'?alpha('#fff',0.02):alpha('#000',0.01))},
                        }}>
                          {/* Play / Stop */}
                          <Tooltip title={isActive?'Ndalo kohëmatësin':'Fillo kohëmatësin'} arrow>
                            <IconButton size="small"
                              onClick={()=>isActive?stopTimer():startFromTask(t)}
                              sx={{
                                width:34,height:34,flexShrink:0,border:'1px solid',
                                borderColor:isActive?'#ef4444':'#059669',
                                color:isActive?'#ef4444':'#059669',
                                bgcolor:isActive?alpha('#ef4444',0.06):alpha('#059669',0.04),
                                '&:hover':{bgcolor:isActive?alpha('#ef4444',0.12):alpha('#059669',0.1)},
                              }}>
                              {isActive?<StopRoundedIcon sx={{fontSize:18}}/>:<PlayArrowRoundedIcon sx={{fontSize:16}}/>}
                            </IconButton>
                          </Tooltip>

                          {/* Title + meta chips */}
                          <Box sx={{flex:1,minWidth:0}}>
                            <Stack direction="row" alignItems="center" spacing={0.7} flexWrap="wrap">
                              <Typography sx={{fontWeight:600,fontSize:'0.84rem',color:isActive?'#059669':'text.primary'}}>{t.Title}</Typography>
                              <Chip label={t.Status} size="small" sx={{bgcolor:alpha(sc,0.1),color:sc,fontWeight:700,fontSize:'0.62rem',height:18}}/>
                              <Chip label={t.Priority} size="small" sx={{bgcolor:alpha(pc,0.1),color:pc,fontWeight:700,fontSize:'0.62rem',height:18}}/>
                              {over&&<Chip label="vonuar" size="small" sx={{height:17,fontSize:'0.6rem',bgcolor:alpha('#ef4444',0.1),color:'error.main',fontWeight:700}}/>}
                            </Stack>
                            <Stack direction="row" spacing={0.8} alignItems="center" sx={{mt:0.35,flexWrap:'wrap'}}>
                              {proj&&<Chip label={proj.Name} size="small" sx={{bgcolor:alpha(proj.Color||'#6366f1',0.1),color:proj.Color||'#6366f1',fontWeight:600,fontSize:'0.62rem',height:17}}/>}
                              {act&&<Chip icon={<BoltIcon sx={{fontSize:'9px !important'}}/>} label={act.title} size="small" sx={{bgcolor:alpha('#8b5cf6',0.1),color:'#8b5cf6',fontWeight:600,fontSize:'0.62rem',height:17}}/>}
                              {due&&<Typography variant="caption" sx={{color:over?'error.main':'text.disabled',fontFamily:'monospace',fontSize:'0.7rem'}}>{due}</Typography>}
                            </Stack>
                          </Box>

                          {/* Total time */}
                          <Stack alignItems="flex-end" sx={{flexShrink:0,minWidth:80}}>
                            {isActive&&(
                              <Typography sx={{fontFamily:'monospace',fontWeight:800,fontSize:'1rem',color:'#059669',lineHeight:1}}>
                                {fmtDur(elapsed)}
                              </Typography>
                            )}
                            {totalTaskTime>0&&(
                              <Typography variant="caption" sx={{color:'text.secondary',fontFamily:'monospace',fontSize:'0.7rem'}}>
                                {fmtDurCompact(totalTaskTime)}
                              </Typography>
                            )}
                          </Stack>

                          {/* Edit / Delete */}
                          <Stack direction="row" spacing={0.2} sx={{flexShrink:0}}>
                            <IconButton size="small" onClick={()=>setTaskDlg({open:true,data:t})} sx={{'&:hover':{color:'primary.main',bgcolor:t=>alpha(t.palette.primary.main,0.08)}}}>
                              <EditOutlinedIcon sx={{fontSize:15}}/>
                            </IconButton>
                            <IconButton size="small" color="error" disabled={isBusy(`tasks:delete:${t.Id}`)} onClick={()=>setDelDlg({open:true,type:'tasks',id:t.Id})} sx={{'&:hover':{bgcolor:t=>alpha(t.palette.error.main,0.08)}}}>
                              <DeleteOutlineIcon sx={{fontSize:15}}/>
                            </IconButton>
                          </Stack>
                        </Box>

                        {/* ── Timeline panel ── */}
                        <Collapse in={isActive}>
                          <Box sx={{
                            mx:2, mb:1.5, mt:0.5, borderRadius:2,
                            border:'1px solid', borderColor:alpha('#059669',0.25),
                            bgcolor:t=>t.palette.mode==='dark'?alpha('#059669',0.05):alpha('#059669',0.02),
                            overflow:'hidden',
                          }}>
                            <LinearProgress sx={{height:2,bgcolor:alpha('#059669',0.1),'& .MuiLinearProgress-bar':{bgcolor:'#059669',transition:'none'}}}/>

                            {/* Active timer row */}
                            <Box sx={{px:2.5,py:1.5,display:'flex',alignItems:'center',gap:2,borderBottom:'1px solid',borderColor:alpha('#059669',0.15)}}>
                              <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:'#059669',flexShrink:0,
                                animation:'pulse 1s ease-in-out infinite',
                                '@keyframes pulse':{'0%,100%':{opacity:1,transform:'scale(1)'},'50%':{opacity:0.5,transform:'scale(1.4)'}},
                              }}/>
                              <Typography sx={{fontFamily:'monospace',fontWeight:800,fontSize:'1.5rem',color:'#059669',letterSpacing:3,lineHeight:1}}>
                                {fmtDur(elapsed)}
                              </Typography>
                              <Typography variant="caption" sx={{color:'text.secondary',flex:1}}>
                                Filloi {tStartDsp?tStartDsp.slice(0,5):''}
                              </Typography>
                              <Button size="small" variant="outlined"
                                startIcon={<StopRoundedIcon sx={{fontSize:14}}/>}
                                onClick={stopTimer}
                                sx={{borderColor:'#ef4444',color:'#ef4444',borderRadius:1.5,fontWeight:700,textTransform:'none','&:hover':{bgcolor:alpha('#ef4444',0.07),borderColor:'#ef4444'}}}>
                                Stop
                              </Button>
                              <Tooltip title="Hiq regjistrimin" arrow>
                                <IconButton size="small" onClick={discardTimer} sx={{color:'text.disabled','&:hover':{color:'error.main'}}}>
                                  <DeleteOutlineIcon sx={{fontSize:15}}/>
                                </IconButton>
                              </Tooltip>
                            </Box>

                            {/* Timeline entries */}
                            {taskEntries.length>0 ? (
                              <Box sx={{px:2.5,py:1.5}}>
                                <Typography variant="caption" sx={{fontWeight:700,color:'text.secondary',textTransform:'uppercase',letterSpacing:'0.08em',fontSize:'0.62rem',display:'block',mb:1.2}}>
                                  Historia · {taskEntries.length} {taskEntries.length===1?'regjistrim':'regjistrime'} · {fmtDurCompact(totalTaskTime)} total
                                </Typography>
                                <Box sx={{position:'relative',pl:3}}>
                                  {/* vertical line */}
                                  <Box sx={{position:'absolute',left:5,top:6,bottom:6,width:2,borderRadius:1,bgcolor:t=>t.palette.mode==='dark'?'grey.800':'#e2e8f0'}}/>
                                  <Stack spacing={1.2}>
                                    {taskEntries.map(e=>(
                                      <Box key={e.id} sx={{display:'flex',alignItems:'flex-start',gap:1.5,position:'relative'}}>
                                        {/* timeline dot */}
                                        <Box sx={{position:'absolute',left:-19,top:4,width:9,height:9,borderRadius:'50%',
                                          bgcolor:e.billable?'#059669':'#94a3b8',
                                          border:'2px solid',borderColor:t=>t.palette.mode==='dark'?'#0f172a':'#fff',
                                          flexShrink:0,
                                        }}/>
                                        <Box sx={{flex:1,minWidth:0}}>
                                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                            <Typography variant="caption" sx={{fontWeight:700,fontFamily:'monospace',color:'text.primary',fontSize:'0.8rem'}}>
                                              {fmtDur(e.duration_seconds)}
                                            </Typography>
                                            <Typography variant="caption" sx={{color:'text.disabled',fontSize:'0.7rem',fontFamily:'monospace'}}>
                                              {e.date}{e.start_time ? ' · '+e.start_time.slice(0,5)+(e.end_time ? ' – '+e.end_time.slice(0,5) : '') : ''}
                                            </Typography>
                                            {e.billable&&<AttachMoneyIcon sx={{fontSize:12,color:'#059669'}}/> }
                                          </Stack>
                                          {e.description&&<Typography variant="caption" sx={{color:'text.secondary',display:'block',fontSize:'0.73rem',mt:0.2}}>{e.description}</Typography>}
                                        </Box>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{px:2.5,py:1.5,textAlign:'center'}}>
                                <Typography variant="caption" sx={{color:'text.disabled'}}>Nuk ka regjistrime të mëparshme për këtë detyrë</Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
            </Box>
          )}

          {/* ── ACTIVITIES ── */}
          {tab===3 && (
            <Box>
              {filteredActivities.length===0
                ? <Box sx={{py:8,textAlign:'center'}}>
                    <BoltIcon sx={{fontSize:48,color:'text.disabled',mb:1}}/>
                    <Typography sx={{color:'text.secondary'}}>Nuk ka aktivitete.</Typography>
                    <Typography variant="caption" sx={{color:'text.disabled',display:'block',mb:2}}>Shto aktivitete për projektet tuaja</Typography>
                    <Button variant="outlined" size="small" startIcon={<AddIcon/>} sx={{borderRadius:2}} onClick={()=>setActDlg({open:true,data:null})}>Aktivitet i ri</Button>
                  </Box>
                : <Box>
                    {/* group activities by project */}
                    {projects.filter(p=>filteredActivities.some(a=>String(a.project_id)===String(p.Id))).map(proj=>{
                      const projActs=filteredActivities.filter(a=>String(a.project_id)===String(proj.Id));
                      return (
                        <Box key={proj.Id}>
                          {/* project header */}
                          <Box sx={{px:2,py:1,display:'flex',alignItems:'center',gap:1.5,bgcolor:t=>t.palette.mode==='dark'?alpha(proj.Color||'#6366f1',0.05):alpha(proj.Color||'#6366f1',0.04),borderBottom:'1px solid',borderColor:'divider'}}>
                            <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:proj.Color||'#6366f1',flexShrink:0}}/>
                            <Typography sx={{fontWeight:700,fontSize:'0.82rem',color:proj.Color||'primary.main'}}>{proj.Name}</Typography>
                            <Typography variant="caption" sx={{color:'text.secondary'}}>{projActs.length} aktivitete</Typography>
                            <Box sx={{flex:1}}/>
                            <Tooltip title="Shto aktivitet" arrow>
                              <IconButton size="small" onClick={()=>setActDlg({open:true,data:{project_id:String(proj.Id)}})} sx={{color:proj.Color||'primary.main',opacity:0.7,'&:hover':{opacity:1}}}>
                                <AddIcon sx={{fontSize:16}}/>
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {/* activity rows */}
                          {projActs.map(a=>{
                            const sc={Active:'#059669','In Progress':'#d97706','On Hold':'#64748b',Done:'#6366f1'}[a.status]||'#64748b';
                            const logS=entries.filter(e=>e.activity_id===a.id).reduce((s,e)=>s+(e.duration_seconds||0),0);
                            const tCnt=tasks.filter(t=>String(t.ActivityId||t.activity_id)===String(a.id)).length;
                            return (
                              <Box key={a.id} sx={{
                                display:'flex',alignItems:'center',gap:1.5,px:3,py:1.2,
                                borderBottom:'1px solid',borderColor:t=>t.palette.mode==='dark'?'grey.900':'grey.100',
                                '&:hover .act-actions':{opacity:1},
                                '&:hover':{bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.015):alpha('#000',0.008)},
                              }}>
                                <BoltIcon sx={{fontSize:14,color:'#8b5cf6',flexShrink:0}}/>
                                <Box sx={{flex:1,minWidth:0}}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{fontWeight:600,fontSize:'0.83rem'}}>{a.title}</Typography>
                                    {tCnt>0&&<Chip label={tCnt+' det.'} size="small" sx={{height:16,fontSize:'0.6rem',bgcolor:alpha('#6366f1',0.1),color:'#6366f1',fontWeight:600}}/>}
                                    {a.group_id && (() => { const grp=groups.find(g=>g.id===a.group_id); return grp ? <Chip label={grp.name} size="small" sx={{height:16,fontSize:'0.6rem',bgcolor:alpha(grp.color||'#8b5cf6',0.12),color:grp.color||'#8b5cf6',fontWeight:600}}/> : null; })()}
                                  </Stack>
                                  {a.description&&<Typography variant="caption" sx={{color:'text.secondary',display:'block'}}>{a.description}</Typography>}
                                </Box>
                                <Chip label={a.status} size="small" sx={{bgcolor:alpha(sc,0.1),color:sc,fontWeight:600,fontSize:'0.68rem',height:19,flexShrink:0}}/>
                                <Typography sx={{fontFamily:'monospace',fontWeight:700,fontSize:'0.82rem',color:logS>0?'primary.main':'text.disabled',minWidth:72,textAlign:'right',flexShrink:0}}>
                                  {logS>0?fmtDur(logS):'—'}
                                </Typography>
                                <Stack direction="row" spacing={0.3} className="act-actions" sx={{opacity:0,transition:'opacity 0.15s',flexShrink:0}}>
                                  <IconButton size="small" onClick={()=>setActDlg({open:true,data:a})} sx={{'&:hover':{color:'primary.main',bgcolor:t=>alpha(t.palette.primary.main,0.08)}}}>
                                    <EditOutlinedIcon sx={{fontSize:15}}/>
                                  </IconButton>
                                  <IconButton size="small" color="error" disabled={isBusy(`taskActivities:delete:${a.id}`)} onClick={()=>setDelDlg({open:true,type:'taskActivities',id:a.id})} sx={{'&:hover':{bgcolor:t=>alpha(t.palette.error.main,0.08)}}}>
                                    <DeleteOutlineIcon sx={{fontSize:15}}/>
                                  </IconButton>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                    {/* activities without a matching project (orphans) */}
                    {filteredActivities.filter(a=>!projects.some(p=>String(p.Id)===String(a.project_id))).map(a=>(
                      <Box key={a.id} sx={{display:'flex',alignItems:'center',gap:1.5,px:2,py:1.2,borderBottom:'1px solid',borderColor:'divider','&:hover .act-actions':{opacity:1}}}>
                        <BoltIcon sx={{fontSize:14,color:'text.disabled'}}/>
                        <Typography sx={{flex:1,fontSize:'0.83rem'}}>{a.title}</Typography>
                        <Stack direction="row" spacing={0.3} className="act-actions" sx={{opacity:0,transition:'opacity 0.15s'}}>
                          <IconButton size="small" onClick={()=>setActDlg({open:true,data:a})}><EditOutlinedIcon sx={{fontSize:15}}/></IconButton>
                          <IconButton size="small" color="error" disabled={isBusy(`taskActivities:delete:${a.id}`)} onClick={()=>setDelDlg({open:true,type:'taskActivities',id:a.id})}><DeleteOutlineIcon sx={{fontSize:15}}/></IconButton>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
              }
            </Box>
          )}

          {/* ── GROUPS ── */}
          {tab===4 && (
            <Box>
              {filteredGroups.length===0
                ? <Box sx={{py:8,textAlign:'center'}}>
                    <FolderOpenIcon sx={{fontSize:48,color:'text.disabled',mb:1}}/>
                    <Typography sx={{color:'text.secondary'}}>Nuk ka grupe.</Typography>
                    <Typography variant="caption" sx={{color:'text.disabled',display:'block',mb:2}}>Shto grupe per projektet tuaja</Typography>
                    <Button variant="outlined" size="small" startIcon={<AddIcon/>} sx={{borderRadius:2}} onClick={()=>setGroupDlg({open:true,data:null})}>Grup i ri</Button>
                  </Box>
                : <Box>
                    {projects.filter(p=>filteredGroups.some(g=>String(g.project_id)===String(p.Id))).map(proj=>{
                      const projGrps=filteredGroups.filter(g=>String(g.project_id)===String(proj.Id));
                      return (
                        <Box key={proj.Id}>
                          <Box sx={{px:2,py:1,display:'flex',alignItems:'center',gap:1.5,bgcolor:t=>t.palette.mode==='dark'?alpha(proj.Color||'#6366f1',0.05):alpha(proj.Color||'#6366f1',0.04),borderBottom:'1px solid',borderColor:'divider'}}>
                            <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:proj.Color||'#6366f1',flexShrink:0}}/>
                            <Typography sx={{fontWeight:700,fontSize:'0.82rem',color:proj.Color||'primary.main'}}>{proj.Name}</Typography>
                            <Typography variant="caption" sx={{color:'text.secondary'}}>{projGrps.length} grupe</Typography>
                            <Box sx={{flex:1}}/>
                            <Tooltip title="Shto grup" arrow>
                              <IconButton size="small" onClick={()=>setGroupDlg({open:true,data:{project_id:String(proj.Id)}})} sx={{color:proj.Color||'primary.main',opacity:0.7,'&:hover':{opacity:1}}}>
                                <AddIcon sx={{fontSize:16}}/>
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {projGrps.map(g=>{
                            const sc={Active:'#059669','On Hold':'#64748b',Done:'#6366f1'}[g.status]||'#64748b';
                            const aCnt=activities.filter(a=>a.group_id===g.id).length;
                            const logS=entries.filter(e=>activities.find(a=>a.id===e.activity_id&&a.group_id===g.id)).reduce((s,e)=>s+(e.duration_seconds||0),0);
                            return (
                              <Box key={g.id} sx={{
                                display:'flex',alignItems:'center',gap:1.5,px:3,py:1.1,
                                borderBottom:'1px solid',borderColor:t=>t.palette.mode==='dark'?'grey.900':'grey.100',
                                '&:hover .grp-actions':{opacity:1},
                                '&:hover':{bgcolor:t=>t.palette.mode==='dark'?alpha('#fff',0.015):alpha('#000',0.008)},
                              }}>
                                <Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:g.color||'#8b5cf6',flexShrink:0}}/>
                                <Box sx={{flex:1,minWidth:0}}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{fontWeight:600,fontSize:'0.83rem'}}>{g.name}</Typography>
                                    {aCnt>0&&<Chip icon={<BoltIcon sx={{fontSize:'10px !important'}}/>} label={aCnt+' akt.'} size="small"
                                      onClick={()=>{setTab(3);setQ(g.name);}}
                                      sx={{height:16,fontSize:'0.6rem',bgcolor:alpha('#8b5cf6',0.1),color:'#8b5cf6',fontWeight:600,cursor:'pointer'}}/>}
                                  </Stack>
                                  {g.description&&<Typography variant="caption" sx={{color:'text.secondary',display:'block'}}>{g.description}</Typography>}
                                </Box>
                                <Chip label={g.status} size="small" sx={{bgcolor:alpha(sc,0.1),color:sc,fontWeight:600,fontSize:'0.68rem',height:19,flexShrink:0}}/>
                                <Typography sx={{fontFamily:'monospace',fontWeight:700,fontSize:'0.82rem',color:logS>0?'primary.main':'text.disabled',minWidth:72,textAlign:'right',flexShrink:0}}>
                                  {logS>0?fmtDur(logS):'—'}
                                </Typography>
                                <Stack direction="row" spacing={0.3} className="grp-actions" sx={{opacity:0,transition:'opacity 0.15s',flexShrink:0}}>
                                  <Tooltip title="Shto aktivitet në këtë grup" arrow>
                                    <IconButton size="small" onClick={()=>setActDlg({open:true,data:{project_id:String(proj.Id),group_id:String(g.id)}})} sx={{'&:hover':{color:'#8b5cf6',bgcolor:alpha('#8b5cf6',0.08)}}}>
                                      <BoltIcon sx={{fontSize:14}}/>
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton size="small" onClick={()=>setGroupDlg({open:true,data:g})} sx={{'&:hover':{color:'primary.main',bgcolor:t=>alpha(t.palette.primary.main,0.08)}}}>
                                    <EditOutlinedIcon sx={{fontSize:15}}/>
                                  </IconButton>
                                  <IconButton size="small" color="error" disabled={isBusy(`activityGroups:delete:${g.id}`)} onClick={()=>setDelDlg({open:true,type:'activityGroups',id:g.id})} sx={{'&:hover':{bgcolor:t=>alpha(t.palette.error.main,0.08)}}}>
                                    <DeleteOutlineIcon sx={{fontSize:15}}/>
                                  </IconButton>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                    {/* groups whose project isn't in the list (orphans) — mirrors
                        the Activities tab. Without this a group renders nowhere
                        at all, so creating one looked like a no-op. */}
                    {filteredGroups.filter(g=>!projects.some(p=>String(p.Id)===String(g.project_id))).map(g=>(
                      <Box key={g.id} sx={{display:'flex',alignItems:'center',gap:1.5,px:2,py:1.2,borderBottom:'1px solid',borderColor:'divider','&:hover .grp-actions':{opacity:1}}}>
                        <Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:g.color||'#8b5cf6',flexShrink:0}}/>
                        <Typography sx={{flex:1,fontSize:'0.83rem',fontWeight:600}}>{g.name}</Typography>
                        <Chip label={g.status} size="small" sx={{fontSize:'0.68rem',height:19,flexShrink:0}}/>
                        <Stack direction="row" spacing={0.3} className="grp-actions" sx={{opacity:0,transition:'opacity 0.15s'}}>
                          <IconButton size="small" onClick={()=>setGroupDlg({open:true,data:g})}><EditOutlinedIcon sx={{fontSize:15}}/></IconButton>
                          <IconButton size="small" color="error" disabled={isBusy(`activityGroups:delete:${g.id}`)} onClick={()=>setDelDlg({open:true,type:'activityGroups',id:g.id})}><DeleteOutlineIcon sx={{fontSize:15}}/></IconButton>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
              }
            </Box>
          )}
        </Box>
      </Box>
      )}

      {/* ── DIALOGS ─────────────────────────────────────────────────────────── */}
      <EntryDialog open={entryDlg.open} initial={entryDlg.data} projects={projects} groups={groups} tasks={tasks} activities={activities}
        onClose={()=>setEntryDlg({open:false,data:null})} onSave={saveEntry} saving={saving}/>
      <ProjectDialog open={projDlg.open} initial={projDlg.data}
        onClose={()=>setProjDlg({open:false,data:null})} onSave={saveProject} saving={saving}/>
      <TaskDialog open={taskDlg.open} initial={taskDlg.data} projects={projects} activities={activities}
        onClose={()=>setTaskDlg({open:false,data:null})} onSave={saveTask} saving={saving}/>
      <ActivityDialog open={actDlg.open} initial={actDlg.data} projects={projects} groups={groups||[]}
        onClose={()=>setActDlg({open:false,data:null})} onSave={saveActivity} saving={saving}/>
      <GroupDialog open={groupDlg.open} initial={groupDlg.data} projects={projects}
        onClose={()=>setGroupDlg({open:false,data:null})} onSave={saveGroup} saving={saving}/>
      <DelDialog open={delDlg.open} onClose={()=>setDelDlg({open:false,type:null,id:null})} onConfirm={handleDelete} saving={saving}/>
      <Snackbar open={!!snack} autoHideDuration={3500} onClose={()=>setSnack('')}
        message={snack} anchorOrigin={{vertical:'bottom',horizontal:'center'}}/>

    </Box>
  );
}
